import { isoDateTime, z } from "../zod.js";
import { Locale, localeKeys } from "../locales.js";
import { Uuid } from "../identity/admin-schemas.js";
import { NUL_ISSUE, textHasNoNul } from "../common/text.js";
import { CustomerNote, CustomerTagRef } from "./crm-schemas.js";

// Module 09 — Sales: the contract DTOs for slice P3-A, SALES FOUNDATIONS
// (spec 09 §2.1–§2.11 + §2.22, adjudicated P3-0 — D-231…D-235): customers,
// contacts + messaging channels, the distributor eligible list, reliability
// snapshots (provisional, §3.9), price lists + items, off-cut discount
// classes, margin guards, tenant sales settings, and the §3.2 pricing
// dry-run. Quotes/orders/shipments DTOs (§2.12–§2.21) are P3-B/P3-C/P3-D and
// are ABSENT, not stubbed — the statement every module's first slice wrote.
//
// `customer_price_overrides` was TABLE-ONLY from P3-A through P3-C-1: the
// §3.2 step-2 engine read it and the resolve response could name it as a
// price source, but no route wrote a row. SLICE P3-C-2 (2026-09-08, D-263)
// puts the §5 override surface on the wire — create / decide / list / revoke
// — and this file carries its DTOs beside the entity: the create request (a
// discriminated union on `override_kind`, so the §2.9 kind-pairing CHECK is
// the request's SHAPE rather than a refusal), the decide and revoke bodies,
// the Customer 360 Pricing tab's list envelope, and the pending-review
// evidence the §4.6 `price_override` card renders.
//
// CONVENTIONS, carried from inventory/waste/production/quality:
//  * EXACTNESS (D-041): quantities and percentages are DECIMAL STRINGS
//    mirroring their numeric(p,s) columns — qty numeric(14,3), discount
//    numeric(5,2), margin numeric(6,2) signed.
//  * MONEY is integer minor units + `currency char(3)`; JSON-safe bound per
//    spec 09 §9.16 — contracts validate < 2^53 because JSON numbers cross the
//    SPA boundary.
//  * REDACTION IS OMISSION (spec 09 §7 literal: "redacted keys are omitted
//    from the payload, never null, never a marker") — hence the `.optional()`
//    money/margin/credit fields on response DTOs below. `margin_breached`
//    stays REQUIRED: a sales user without `sales.margin.read` sees the
//    boolean and never the numbers (§4.4).
//  * Lists answer `{items, next_cursor}` envelopes, never bare arrays.
//  * D-225: no server-minted prose — every refusal and every degradation is a
//    structured code (the problem catalog at the end) or a typed field.

// --- shared scalars --------------------------------------------------------

/** Integer minor units (money) — never a float (the money convention); < 2^53 because
 * JSON numbers cross the SPA boundary (spec 09 §9.16). */
export const SalesMinorMoney = z
  .number()
  .int()
  .max(Number.MAX_SAFE_INTEGER)
  .min(-Number.MAX_SAFE_INTEGER);
/** Non-negative minor units — prices, limits. */
export const SalesMinorMoneyNonNegative = SalesMinorMoney.min(0);
export const SalesCurrency = z
  .string()
  .regex(/^[A-Z]{3}$/)
  .openapi({ example: "KZT", description: "ISO 4217 (char(3))" });
export const SalesCountry = z
  .string()
  .regex(/^[A-Z]{2}$/)
  .openapi({ example: "KZ", description: "ISO 3166-1 alpha-2 (char(2))" });
/**
 * ISO-8601 business date, evaluated in tenant HQ timezone where §9.14 says so.
 *
 * The `.date()` check is CALENDAR validity on top of the shape: the regex
 * alone admits `2026-02-30` and `2026-13-45`, and every consumer of this type
 * hands the string to Postgres as `$n::date` / `$n::timestamptz`, where an
 * impossible date raises 22008 exactly as `abc` raises 22007 — an unmapped
 * SQLSTATE, so the caller's malformed input answers 500 INTERNAL instead of
 * 400. Both belong to the caller. The regex STAYS beside it so the generated
 * OpenAPI keeps its `pattern` (zod-to-openapi emits nothing for `.date()`),
 * which is what documents the shape to a client that never sees this file.
 */
export const SalesBusinessDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .check(z.iso.date())
  .openapi({ format: "date", example: "2026-09-01" });

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const INSTANT = /^\d{4}-\d{2}-\d{2}[Tt ]\d{2}:\d{2}(:\d{2}(\.\d{1,6})?)?([Zz]|[+-]\d{2}:?\d{2})?$/;

/**
 * True for the values a sales list route's date filter may carry: an ISO-8601
 * calendar date, or an ISO-8601 instant. Exported so the API's own controller
 * tests can pin the accepted set without reaching into the schema.
 */
export function isSalesDateFilter(value: string): boolean {
  if (!DATE_ONLY.test(value) && !INSTANT.test(value)) return false;
  const year = Number(value.slice(0, 4));
  const month = Number(value.slice(5, 7));
  const day = Number(value.slice(8, 10));
  if (month < 1 || month > 12 || day < 1 || day > 31) return false;
  // Round-trip through UTC so 2026-02-30 and 2026-04-31 are refused HERE and
  // not by Postgres as an unmapped 22008. `setUTCFullYear` rather than the
  // `Date.UTC` constructor: the latter folds a two-digit year into 19xx, and
  // the regex above admits `0050-01-01`.
  const probe = new Date(0);
  probe.setUTCFullYear(year, month - 1, day);
  if (probe.getUTCMonth() !== month - 1 || probe.getUTCDate() !== day) return false;
  if (value.length === 10) return true;
  const hour = Number(value.slice(11, 13));
  const minute = Number(value.slice(14, 16));
  if (hour > 23 || minute > 59) return false;
  const second = value.charAt(16) === ":" ? Number(value.slice(17, 19)) : 0;
  // 60 is a leap second, which Postgres accepts.
  return second <= 60;
}

/**
 * A list route's `?…_from=` / `?…_to=` filter — the value a sales repo binds
 * STRAIGHT into SQL as `$n::timestamptz` (the quote list's `created_at`) or
 * `$n::date` (the order list's `promise_date`).
 *
 * WHY IT EXISTS. These four parameters were declared `z.string().optional()`
 * until 2026-09-10, so `abc`, the empty string and `2026-02-30` reached
 * Postgres, raised SQLSTATE 22007 / 22008 — which the platform's problem
 * filter does not map — and answered the caller's own typo with **500
 * INTERNAL**. The same defect class as the malformed-uuid filters that were
 * fixed on modules 04/06.
 *
 * WHY IT IS NOT {@link SalesBusinessDate}. That type is a business DATE, and
 * adopting it here would also refuse `2026-09-01T09:00:00Z`, which works
 * today: a `timestamptz` column takes it and `::date` casts it. Refusing a
 * value a shipped parameter accepts is a contract narrowing; this is a defect
 * fix, so the accepted set is widened to exactly what already works and
 * nothing more.
 *
 * WHY THE CHECK IS A REFINEMENT AND NOT `.regex()`. zod-to-openapi emits a
 * `pattern` (and `.date()` a `format`) into the parameter schema, and a
 * pattern added to a published query parameter is what `oasdiff breaking`
 * reports as `request-parameter-pattern-added` — a BREAKING change under
 * TESTING §6 that needs an entry in `openapi/breaking-approvals.json`. A refinement
 * emits nothing, so `openapi/v1.json` keeps the exact parameter schema it
 * already published while the door stops answering 500. The accepted set is
 * stated in the description below, where a client reads it.
 *
 * The issue it raises is a plain `invalid_format`, which `wireIssueCode`
 * puts on the wire as `invalid_string`, so the 400 carries the
 * `validation.invalid_string` message_key every other malformed string on the
 * platform carries (D-022: keys, never prose).
 */
export const SalesDateFilter = z
  .string()
  .superRefine((value, ctx) => {
    if (isSalesDateFilter(value)) return;
    ctx.addIssue({
      code: "invalid_format",
      format: "regex",
      message: "expected an ISO-8601 date (2026-09-01) or instant (2026-09-01T09:00:00Z)",
    });
  })
  .openapi({
    example: "2026-09-01",
    description:
      "ISO-8601 date `YYYY-MM-DD`, or a full instant such as `2026-09-01T09:00:00Z`. " +
      "An impossible calendar date, an empty string or any other text is refused 400 " +
      "VALIDATION_FAILED on this field.",
  });
/** A strictly positive numeric(14,3) quantity as exact decimal text (D-041). */
export const SalesQty = z
  .string()
  .regex(/^(?=.*[1-9])\d{1,11}(\.\d{1,3})?$/)
  .openapi({ example: "100.000", description: "Exact decimal, ≤ 3 dp, > 0 (numeric(14,3))" });
/**
 * A PHYSICAL MEASURE as module 06 keeps it — `numeric(18,6)`, up to SIX
 * decimals, > 0. Not the same thing as `SalesQty`.
 *
 * WHY THE DISTINCTION IS NOT PEDANTRY (D-265). An off-cut piece's `length`
 * belongs to module 06: `stock_units.remaining_measure` is `numeric(18,6)`
 * and serves "43.000000". Declaring it `SalesQty` (≤ 3 dp) made the API
 * produce a value it then REFUSED: `POST /v1/sales/fulfillment-options`
 * returned the piece, the composer posted it straight back in the line's
 * `fulfillment_plan`, and the request schema answered 400 VALIDATION_FAILED
 * on `pieces.0.length` — so no quote line could ever be saved with a named
 * off-cut combo. The response side lied silently (responses are not
 * runtime-validated), which is why it took a walkthrough to find.
 *
 * Sales quantities stay `SalesQty`: a quote line's `qty` IS this module's own
 * `numeric(14,3)`. Only measures borrowed from 06 use this.
 */
export const SalesMeasure = z
  .string()
  .regex(/^(?=.*[1-9])\d{1,11}(\.\d{1,6})?$/)
  .openapi({
    example: "43.000000",
    description: "Exact decimal, ≤ 6 dp, > 0 — module 06's numeric(18,6) measure frame",
  });

/** A zero-allowed numeric(14,3) quantity (price-break lower bounds). */
export const SalesQtyOrZero = z
  .string()
  .regex(/^\d{1,11}(\.\d{1,3})?$/)
  .openapi({ example: "0.000", description: "Exact decimal, ≤ 3 dp, ≥ 0 (numeric(14,3))" });
/**
 * A SIGNED quantity at 3 dp, for a figure this module reports and never
 * stores: module 06's available-to-promise, which goes negative when holds
 * exceed stock (spec 06 §3.6). Up to twelve integer digits, the magnitude of
 * module 06's numeric(18,6) frame. D-339.
 */
export const SalesSignedQty = z
  .string()
  .regex(/^-?\d{1,12}(\.\d{1,3})?$/)
  .openapi({
    example: "-12.500",
    description: "Exact decimal, ≤ 3 dp, signed (a reported figure, never stored)",
  });
/** A numeric(5,2) percentage 0–100 as exact decimal text (D-041). */
export const SalesPct = z
  .string()
  .regex(/^(100(\.00?)?|\d{1,2}(\.\d{1,2})?)$/)
  .openapi({ example: "30.00", description: "Exact decimal percent 0–100 (numeric(5,2))" });
/** A SIGNED numeric(6,2) percentage — margins go negative below cost (§3.2). */
export const SalesSignedPct = z
  .string()
  .regex(/^-?\d{1,4}(\.\d{1,2})?$/)
  .openapi({ example: "-12.50", description: "Exact decimal percent, signed (numeric(6,2))" });

/** Locale bundle over the registry, `en` required non-empty — the 0042/D-209
 * shape, restated rather than imported so another module's catalog contract
 * cannot silently widen this one. */
export const SalesLocaleName = z
  .object({
    ...localeKeys(z.string().min(1).max(200).optional()),
    en: z.string().min(1).max(200),
  })
  .strict()
  .openapi("SalesLocaleName");

// --- closed vocabularies (spec 09 §2) --------------------------------------

export const CustomerTier = z.enum(["market", "b2b", "distributor"]);
export const CustomerStatus = z.enum(["active", "archived"]);
export const ContactChannelKind = z.enum(["phone", "telegram", "whatsapp", "email"]);
export const PreferredChannel = z.enum(["telegram", "whatsapp", "email", "none"]);
export const PreferredLocale = Locale;
export const ConsentStatus = z.enum(["unknown", "opted_in", "opted_out"]);
export const AssignmentRole = z.enum(["owner", "collaborator"]);
/** D-234: whether a list's item prices carry VAT. */
export const VatMode = z.enum(["inclusive", "exclusive"]);
export const PriceListStatus = z.enum(["draft", "active", "archived"]);
export const OverrideKind = z.enum(["fixed_price", "discount_pct"]);
export const OverrideStatus = z.enum(["pending_approval", "active", "expired", "revoked"]);
export const MarginGuardScope = z.enum(["product", "family", "default"]);
/** D-242 — the typed sort surface of `GET /v1/customers` (`-` prefix = desc).
 * `-created_at` is the DEFAULT, applied by the SERVER when the param is
 * omitted: it is today's implicit order declared, so an order-less request
 * stays byte-identical (D-242.3). `-reliability_score` sorts NULLS LAST in
 * both directions (unscored customers trail, never lead). `customer_no` is
 * lexicographic over the zero-padded `CUS-{seq5}` shape — identical to
 * numeric order until the 100,000th customer widens the series to 6 digits
 * (recorded caveat, deliberately not engineered for).
 *
 * EXCLUDED, deliberately (the wave-1b adjudication):
 *  - `tier` — sorting would follow the raw enum token, not the translated
 *    chip a Russian reader sees: a dishonest order. The `tier` filter Select
 *    answers the need.
 *  - `status` — two values, already a filter.
 *  - `credit_limit_minor` — §7-redacted BY OMISSION for most roles; a sort
 *    would leak relative credit magnitude through row order to the very
 *    principals the redaction hides the value from (D-243's fence argument
 *    applied to sort).
 *  - `open_ar` / owner name — no wire column / cross-table; not honest-cheap. */
export const CustomersOrder = z
  .enum([
    "-created_at",
    "created_at",
    "display_name",
    "-display_name",
    "customer_no",
    "-customer_no",
    "updated_at",
    "-updated_at",
    "reliability_score",
    "-reliability_score",
  ])
  .openapi("CustomersOrder");
/** `moving_avg` is a legal COLUMN value; WRITING it is refused 422
 * COST_BASIS_UNAVAILABLE until module 11's cost ledger lands (P4) — spec 09
 * §2.11's stated degradation. */
export const CostBasis = z.enum(["standard", "moving_avg"]);
/** §3.2's resolution provenance (also §2.14 `price_source` when quotes land). */
export const PriceSource = z.enum(["tier_list", "customer_override", "offcut_class", "manual"]);

/** §2.18's `shipments.status` CHECK verbatim (P3-D PR-D3, D-286). `picking`
 * and `packed` are the two OPEN states — one open shipment per order is a
 * partial UNIQUE over exactly these (migration 0063, ruling 3). */
export const ShipmentStatus = z
  .enum(["picking", "packed", "shipped", "delivered", "cancelled"])
  .openapi("ShipmentStatus");

/** WHO said a shipment was delivered (§2.18 `delivered_by`, §6
 * `sales.shipment_delivered.confirmed_by` — D-286 ruling 14): `customer` is
 * the tracking page's own press and the source of truth; `carrier` is the
 * office's `POST /v1/orders/{id}/mark-delivered` on a carrier's word; `auto`
 * is the sales engine's auto-confirm `delivery_auto_confirm_days` after
 * `shipped_at` (P3-E PR-E2, D-292 ruling 10 — its producer since then). */
export const DeliveredBy = z.enum(["customer", "carrier", "auto"]).openapi("DeliveredBy");

// --- addresses -------------------------------------------------------------

/** §2.1 `{line1,line2,city,region,postal,country}` — all optional; the column
 * DEFAULT is `{}`. */
export const SalesAddress = z
  .object({
    line1: z.string().max(200).optional(),
    line2: z.string().max(200).optional(),
    city: z.string().max(100).optional(),
    region: z.string().max(100).optional(),
    postal: z.string().max(20).optional(),
    country: SalesCountry.optional(),
  })
  .strict()
  .openapi("SalesAddress");

// --- customers (§2.1–§2.6) -------------------------------------------------

/** §3.5b/§4.1/§4.2 — the open-AR read with its module-11 degradation carried
 * STRUCTURALLY (D-198 + D-225: a chip is client prose over a typed fact, never
 * server prose). P3: `available: false` and no amount; when module 11 lands,
 * `available: true` with the pair, still governed by `sales.read_value`. */
export const OpenArState = z
  .object({
    available: z.boolean().openapi({
      description: "false until module 11 provides open AR (spec 09 §3.5b stated degradation)",
    }),
    open_ar_minor: SalesMinorMoney.optional(),
    currency: SalesCurrency.optional(),
  })
  .openapi("OpenArState");

const CUSTOMER_NOTES_ALIAS_DESCRIPTION =
  "Since P3-E2 PR E2-2a1 an alias of the customer's PINNED NOTE (spec 09 §2.26, D-323 ruling 2): the pinned note's body, or null while no note is pinned or while the pinned note is redacted. A create or PATCH that sends `notes` appends a typed note and pins it, and `null`, `\"\"` or a value that is empty after trim unpins the pinned note. The notes themselves are `GET /v1/customers/{id}/notes`.";

/** The customer's `notes` alias as every read serves it. */
const CustomerNotesAlias = z.string().nullable();

export const Customer = z
  .object({
    id: Uuid,
    customer_no: z.string().regex(/^CUS-\d{5,}$/),
    legal_name: z.string(),
    display_name: z.string(),
    tax_id: z.string().nullable(),
    country: SalesCountry,
    tier: CustomerTier,
    default_currency: SalesCurrency,
    // §7 field redaction: credit_limit_minor visible to ga/fa/finance/sales
    // (assigned) — OMITTED for everyone else, never null.
    credit_limit_minor: SalesMinorMoneyNonNegative.optional(),
    credit_terms_days: z.number().int().min(0).max(180),
    credit_hold: z.boolean(),
    billing_address: SalesAddress,
    shipping_address: SalesAddress,
    preferred_channel: PreferredChannel,
    preferred_locale: PreferredLocale,
    // The write doors answer this object, and an idempotent replay of any of
    // them omits `notes` (the store keeps no note text, spec 09 §2.26), so it
    // is optional here. The reads spread this shape and put it back as
    // required (`CustomerWithNextStep`). D-339.
    notes: CustomerNotesAlias.optional().openapi({
      description: `${CUSTOMER_NOTES_ALIAS_DESCRIPTION} Absent on an idempotent replay of a create, PATCH, archive or restore, because the replay store keeps no note text (spec 09 §2.26, D-339); the client reads the customer again.`,
    }),
    // NULL until module 11 history exists, except seeded demo scores (§3.9).
    reliability_score: z.number().int().min(0).max(100).nullable(),
    // ALWAYS true in P3 — "provisional: history starts with module 11"
    // (§3.9's stated degradation; seeded scores included). The flag flips per
    // customer when 11's job computes ≥ 3 paid invoices.
    reliability_provisional: z.boolean(),
    // The §2.2 owner assignment, denormalized into the read model (§4.1's
    // "owner" column). NULL when no owner row exists yet.
    owner_user_id: Uuid.nullable(),
    status: CustomerStatus,
    created_by: Uuid,
    created_at: isoDateTime(),
    updated_at: isoDateTime(),
  })
  .openapi("Customer");

// `CustomerListResponse` and `Customer360Response` are declared after the
// sales-activities block below, because since P3-E2 PR E2-1 their customer
// carries its next step (`CustomerWithNextStep`), which is built from the
// task vocabulary declared there.

export const CustomerContact = z
  .object({
    id: Uuid,
    customer_id: Uuid,
    full_name: z.string(),
    position: z.string().nullable(),
    is_primary: z.boolean(),
    status: CustomerStatus,
    created_at: isoDateTime(),
    updated_at: isoDateTime(),
  })
  .openapi("CustomerContact");

export const ContactChannel = z
  .object({
    id: Uuid,
    contact_id: Uuid,
    customer_id: Uuid,
    channel: ContactChannelKind,
    identity_value: z.string().openapi({
      description:
        "The §2.4 normalized address. In clear only for a caller holding `messaging.address.read`; masked for any other caller with spec 17 §7's forms (`+7•••••1234` for phone and WhatsApp, `chat:•••482` for Telegram, `•••@domain` for email) (spec 09 §7, D-307).",
    }),
    is_verified: z.boolean(),
    consent_status: ConsentStatus,
    created_at: isoDateTime(),
    updated_at: isoDateTime(),
  })
  .openapi("ContactChannel");

export const CustomerAssignment = z
  .object({
    user_id: Uuid,
    role: AssignmentRole,
    created_at: isoDateTime(),
  })
  .openapi("CustomerAssignment");

export const CustomerDistributorProduct = z
  .object({
    id: Uuid,
    customer_id: Uuid,
    product_id: Uuid,
    added_by: Uuid,
    note: z.string().nullable(),
    created_at: isoDateTime(),
  })
  .openapi("CustomerDistributorProduct");

/** §2.6 snapshot. The FORMULA INPUTS (§7 "reliability formula inputs") are
 * redacted by omission outside ga/fa/finance/sales(assigned); `score` itself
 * renders on every list chip. `provisional` is §3.9's typed fact. */
export const CustomerReliabilitySnapshot = z
  .object({
    id: Uuid,
    customer_id: Uuid,
    as_of_date: SalesBusinessDate,
    invoices_paid: z.number().int().min(0).optional(),
    invoices_paid_on_time: z.number().int().min(0).optional(),
    avg_days_late: z
      .string()
      .regex(/^\d{1,4}(\.\d{1,2})?$/)
      .optional(),
    max_days_late: z.number().int().optional(),
    open_ar_minor: SalesMinorMoney.optional(),
    currency: SalesCurrency,
    score: z.number().int().min(0).max(100),
    provisional: z.boolean(),
    created_at: isoDateTime(),
  })
  .openapi("CustomerReliabilitySnapshot");

export const CustomerReliabilityHistoryResponse = z
  .object({ items: z.array(CustomerReliabilitySnapshot) })
  .openapi("CustomerReliabilityHistoryResponse");

const contactChannelSeed = z
  .object({
    channel: ContactChannelKind,
    identity_value: z.string().min(1).max(200),
  })
  .strict();

const contactSeed = z
  .object({
    full_name: z.string().min(1).max(200),
    position: z.string().max(100).optional(),
    is_primary: z.boolean().optional(),
    channels: z.array(contactChannelSeed).max(10).optional(),
  })
  .strict();

/** §3.1 step 1 — create carries contacts + channels inline. `country` and
 * `default_currency` default to the TENANT'S values service-side (§2.1). */
export const CustomerCreateRequest = z
  .object({
    legal_name: z.string().min(1).max(300),
    display_name: z.string().min(1).max(200),
    tax_id: z.string().max(40).optional(),
    country: SalesCountry.optional(),
    tier: CustomerTier,
    default_currency: SalesCurrency.optional(),
    credit_limit_minor: SalesMinorMoneyNonNegative.optional(),
    credit_terms_days: z.number().int().min(0).max(180).optional(),
    credit_hold: z.boolean().optional(),
    billing_address: SalesAddress.optional(),
    shipping_address: SalesAddress.optional(),
    preferred_channel: PreferredChannel.optional(),
    preferred_locale: PreferredLocale.optional(),
    notes: z.string().max(4000).refine(textHasNoNul, NUL_ISSUE).optional(),
    contacts: z.array(contactSeed).max(20).optional(),
  })
  .strict()
  .openapi("CustomerCreateRequest");

/** The typed codes `POST /v1/customers` may name in its 201's
 * `X-Sales-Warning` header, IN THE ORDER the header lists them (spec 09 §3.13,
 * §5's E2-2 row 18, D-324 ruling 3). Since P3-E2 PR E2-2c the header is a
 * comma-separated list, `NO_PRICE_LIST_FOR_CURRENCY` (§9.13) first and then
 * `POSSIBLE_DUPLICATE` (§3.13). A header is a warning and never a problem
 * `code`, so these stay out of `salesProblemCodes`; the server builds the list
 * by filtering this array, and the web localizes each code (D-225). */
export const customerCreateWarningCodes = [
  "NO_PRICE_LIST_FOR_CURRENCY",
  "POSSIBLE_DUPLICATE",
] as const;
export type CustomerCreateWarningCode = (typeof customerCreateWarningCodes)[number];

/** §5 PATCH — profile fields for sales (assigned); credit fields ga/fa/
 * finance; tier ga/fa (each narrowing service-enforced, D-040 two-layer;
 * tier-change rules per §3.1). */
export const CustomerUpdateRequest = z
  .object({
    legal_name: z.string().min(1).max(300).optional(),
    display_name: z.string().min(1).max(200).optional(),
    tax_id: z.string().max(40).nullable().optional(),
    country: SalesCountry.optional(),
    tier: CustomerTier.optional(),
    default_currency: SalesCurrency.optional(),
    credit_limit_minor: SalesMinorMoneyNonNegative.optional(),
    credit_terms_days: z.number().int().min(0).max(180).optional(),
    credit_hold: z.boolean().optional(),
    billing_address: SalesAddress.optional(),
    shipping_address: SalesAddress.optional(),
    preferred_channel: PreferredChannel.optional(),
    preferred_locale: PreferredLocale.optional(),
    notes: z.string().max(4000).refine(textHasNoNul, NUL_ISSUE).nullable().optional(),
    owner_user_id: Uuid.optional(),
  })
  .strict()
  .openapi("CustomerUpdateRequest");

export const ContactCreateRequest = z
  .object({
    full_name: z.string().min(1).max(200),
    position: z.string().max(100).optional(),
    is_primary: z.boolean().optional(),
  })
  .strict()
  .openapi("ContactCreateRequest");

/** §5 PATCH /v1/contacts/{id} — archiving (status='archived') clears
 * is_primary when the contact was primary and emits
 * `sales.customer_contact_deleted` (17 clears channel routing). */
export const ContactUpdateRequest = z
  .object({
    full_name: z.string().min(1).max(200).optional(),
    position: z.string().max(100).nullable().optional(),
    is_primary: z.boolean().optional(),
    status: CustomerStatus.optional(),
  })
  .strict()
  .openapi("ContactUpdateRequest");

/** §5 POST /v1/contacts/{id}/channels. `identity_value` is normalized
 * service-side before insert (§2.4: E.164 / telegram chat id text /
 * lowercased email); the stored, normalized form comes back on the DTO. */
export const ChannelCreateRequest = contactChannelSeed.openapi("ChannelCreateRequest");

export const ChannelDeleteResponse = z
  .object({ id: Uuid, deleted: z.literal(true) })
  .openapi("ChannelDeleteResponse");

export const DistributorProductAddRequest = z
  .object({
    product_id: Uuid,
    note: z.string().max(500).optional(),
  })
  .strict()
  .openapi("DistributorProductAddRequest");

export const DistributorProductRemoveResponse = z
  .object({ customer_id: Uuid, product_id: Uuid, removed: z.literal(true) })
  .openapi("DistributorProductRemoveResponse");

/** §5 POST /v1/customers/resolve-identity — module 17's and the agent's
 * identity→customer hop. 404 (never a different error for "wrong tenant" —
 * §9.15's row-or-nothing rule) when no channel row matches. */
export const ResolveIdentityRequest = z
  .object({
    channel: ContactChannelKind,
    identity_value: z.string().min(1).max(200),
  })
  .strict()
  .openapi("ResolveIdentityRequest");

/** The answer names a customer that was never merged. Since P3-E2 PR E2-2b
 * the door follows `merged_into_customer_id` one hop (§5's E2-2 row 23), and
 * one hop is always enough because a later merge re-points every older
 * tombstone at the new winner (§3.13), so `customer_id` and `customer_status`
 * are the winner's when the channel's customer was merged. */
export const ResolveIdentityResponse = z
  .object({
    customer_id: Uuid,
    contact_id: Uuid,
    customer_status: CustomerStatus,
  })
  .openapi("ResolveIdentityResponse");

// --- price lists (§2.7–§2.8) -----------------------------------------------

export const PriceList = z
  .object({
    id: Uuid,
    code: z.string(),
    name: z.string(),
    tier: CustomerTier,
    currency: SalesCurrency,
    vat_mode: VatMode,
    valid_from: SalesBusinessDate,
    valid_to: SalesBusinessDate.nullable(),
    status: PriceListStatus,
    activated_by: Uuid.nullable(),
    activated_at: isoDateTime().nullable(),
    created_at: isoDateTime(),
    updated_at: isoDateTime(),
  })
  .openapi("PriceList");

export const PriceListListResponse = z
  .object({ items: z.array(PriceList), next_cursor: z.string().nullable() })
  .openapi("PriceListListResponse");

export const PriceListItem = z
  .object({
    id: Uuid,
    price_list_id: Uuid,
    product_id: Uuid,
    uom_id: Uuid,
    min_qty: SalesQtyOrZero,
    unit_price_minor: SalesMinorMoneyNonNegative,
    // §4.3 margin preview vs current standard cost — PRESENT only for
    // holders of `sales.margin.read` (§7 redaction row 1, omission style).
    margin_preview_pct: SalesSignedPct.optional(),
    created_at: isoDateTime(),
    updated_at: isoDateTime(),
  })
  .openapi("PriceListItem");

/** GET /v1/price-lists/{id} — head + items. NOT a §5 row verbatim: §4.3's
 * editable grid and margin preview need an item read §5 forgot to print; the
 * route is added as the §5 table's necessary read (logged in the P3-A
 * decisions candidates; spec table row owed in the same PR). */
export const PriceListDetailResponse = z
  .object({
    price_list: PriceList,
    items: z.array(PriceListItem),
  })
  .openapi("PriceListDetailResponse");

/** §5 POST /v1/price-lists — `vat_mode` optional: the API defaults by tier
 * (market → inclusive, b2b/distributor → exclusive; D-234's regional
 * convention, flipped as data). */
export const PriceListCreateRequest = z
  .object({
    code: z.string().min(1).max(60),
    name: z.string().min(1).max(200),
    tier: CustomerTier,
    currency: SalesCurrency,
    vat_mode: VatMode.optional(),
    valid_from: SalesBusinessDate,
    valid_to: SalesBusinessDate.optional(),
  })
  .strict()
  .openapi("PriceListCreateRequest");

const priceListItemUpsertRow = z
  .object({
    product_id: Uuid,
    uom_id: Uuid,
    min_qty: SalesQtyOrZero.optional(),
    unit_price_minor: SalesMinorMoneyNonNegative,
  })
  .strict();

/** §5 PUT /v1/price-lists/{id}/items — bulk upsert (drafts only; the Excel
 * import lane reuses module 02's row-validation UX). Replaces the list's item
 * set: rows absent from the payload are deleted. */
export const PriceListItemsPutRequest = z
  .object({
    items: z.array(priceListItemUpsertRow).min(1).max(5000),
  })
  .strict()
  .openapi("PriceListItemsPutRequest");

/** The §5 row-validation REPORT codes (D-225 structured, never prose). These
 * are per-row entries of a 200 report body, NOT RFC 9457 problem `code`s —
 * which is why they live here beside their schema rather than in
 * `salesProblemCodes` below (PRODUCT_NOT_FOUND / UOM_NOT_FOUND overlap that
 * catalog because the same fact is also a whole-request refusal elsewhere).
 * Exported so the module-catalog parity gate can hold the module's raise
 * sites against the contract's own enum instead of an unchecked carve-out. */
export const priceListRowRejectionCodes = [
  "PRODUCT_NOT_FOUND",
  "UOM_NOT_FOUND",
  "UOM_NOT_CONVERTIBLE",
  "DUPLICATE_BREAK",
] as const;

/** Row-level validation report (§5): applied wholesale or rejected row by
 * row with structured codes (D-225) — never a prose sentence. */
export const PriceListItemsPutResponse = z
  .object({
    applied: z.number().int().min(0),
    deleted: z.number().int().min(0),
    rejected_rows: z.array(
      z.object({
        index: z.number().int().min(0),
        code: z.enum(priceListRowRejectionCodes),
        product_id: Uuid.optional(),
      }),
    ),
  })
  .openapi("PriceListItemsPutResponse");

// --- customer price overrides (§2.9; the §5 surface — slice P3-C-2, D-263) --

/**
 * The §3.4 guard verdict on a PENDING override — the breach evidence the
 * §4.6 `price_override` card renders ("margin vs guard, impact"), computed
 * SERVER-side at creation by running the §3.2 engine against the override
 * as if it were a line, and served ONLY while `status = 'pending_approval'`.
 *
 * WHY IT IS COMPUTED AND NOT STORED. §2.9 carries no margin, cost or guard
 * column (the D-255 amendment's point about §2.12, applied here): a decided
 * override's record lives in `sales.override_review_decided` and the audit
 * chain, and nowhere a document read can reach. So the decide route
 * RE-EVALUATES the verdict against the current cost and list — exactly as
 * the quote send path re-evaluates a revision regardless of prior approval
 * (§3.4) — and this object states the FRAME the creation-time verdict was
 * measured in so a reader can reproduce it through `POST /v1/pricing/resolve`.
 *
 * THE FRAME. A `fixed_price` override is authored per the product's BASE UoM
 * (§2.9, D-237) and judged there: `evaluated_qty` is the override's own
 * `min_qty` when positive, else one base unit — the smallest quantity the
 * override applies at, which is where a discount bites hardest against a
 * tier list's quantity breaks. `evaluated_date` is the later of `valid_from`
 * and the tenant-local today: an override cannot be judged against a list
 * that is not yet in force on its first effective day. The tier list
 * resolved in that frame is the step-1 price a `discount_pct` override
 * discounts; a `fixed_price` override replaces it.
 *
 * §7 REDACTION, BY OMISSION (D-225): `list_price_minor` and
 * `effective_unit_price_minor` ride `sales.read_value`; `unit_cost_minor`,
 * `margin_pct`, `guard_pct` and `delta_minor` ride `sales.margin.read`. A
 * proposer holding neither still sees `required_approver` and the frame — that
 * the row needs approval and by whom, never the numbers (§4.4's rule). The
 * margin fields are ALSO absent when the cost basis is unavailable (no
 * released standard cost, no UoM conversion path, cross-currency cost — the
 * §2.14 rule: an unprovable margin is not 0.00) — the guard then failed
 * CLOSED, which is why the row is pending at all.
 */
/** The §3.4 threshold tier a review decision needs — `facility_admin` when
 * every breach is ≤2pp and no breached line exceeds the value bar,
 * `group_admin` above it or when the margin could not be proved (the safe
 * direction). ONE named const so the override review, the direct order's
 * review (P3-D-1, D-283) and the web's i18n dynamic-key net all read the same
 * two words; deliberately NOT `.openapi()`-registered, so it stays an inline
 * enum on every payload exactly as it was before it had a name (no schema
 * moves to a `$ref`, no client type changes shape). */
export const RequiredApprover = z.enum(["facility_admin", "group_admin"]);

export const CustomerPriceOverrideReview = z
  .object({
    /** Which §3.4 threshold tier the decision needs — `facility_admin` when
     * the breach is ≤2pp, `group_admin` above it or when the margin could not
     * be proved (the safe direction). Computed server-side, enforced by the
     * decide route (403 APPROVAL_THRESHOLD_EXCEEDED), never re-derived by a
     * client. */
    required_approver: RequiredApprover,
    /** The base-UoM quantity the guard ran at (`min_qty`, else one unit). */
    evaluated_qty: SalesQty,
    /** The business date the tier list was resolved at. */
    evaluated_date: SalesBusinessDate,
    /** The step-1 tier price in the frame; absent without `sales.read_value`,
     * and null when no list row prices the product for this customer (a
     * `fixed_price` override still evaluates against cost alone). */
    list_price_minor: SalesMinorMoneyNonNegative.nullable().optional(),
    /** The override applied in the frame — `unit_price_minor` itself for
     * `fixed_price`, the discounted step-1 price for `discount_pct`. Absent
     * without `sales.read_value`. */
    effective_unit_price_minor: SalesMinorMoneyNonNegative.optional(),
    unit_cost_minor: SalesMinorMoneyNonNegative.optional(),
    margin_pct: SalesSignedPct.optional(),
    guard_pct: SalesPct.optional(),
    /** Minor units per base unit the override price must GAIN to clear the
     * guard — the card's "impact" (§4.6), server-computed so raw cost never
     * has to reach a surface that lacks `sales.margin.read`. */
    delta_minor: SalesMinorMoneyNonNegative.optional(),
  })
  .openapi("CustomerPriceOverrideReview");

/**
 * The §2.9 row. `unit_price_minor` and `discount_pct` are each NULL for the
 * kind that does not carry them (the §2.9 kind-pairing CHECK) and OMITTED —
 * both of them, whatever the kind — for a reader without `sales.read_value`
 * (§7's field grant, D-225: omission, never null, never a marker). The two
 * absences mean different things and are spelled differently on purpose:
 * `null` is "this kind has no such number", a missing key is "you may not
 * see it". `review` is present only while `pending_approval` (see
 * `CustomerPriceOverrideReview`). After a REJECTION the row is `revoked` and
 * `reason` carries the decider's note (spec 09 §5's "reject → status
 * `revoked` with the note as reason"); the proposer's original justification
 * is in the audit chain and the decided event, not re-readable here.
 */
export const CustomerPriceOverride = z
  .object({
    id: Uuid,
    customer_id: Uuid,
    product_id: Uuid,
    override_kind: OverrideKind,
    unit_price_minor: SalesMinorMoneyNonNegative.nullable().optional(),
    discount_pct: SalesPct.nullable().optional(),
    currency: SalesCurrency,
    /** D-241: the base-UoM lower bound this override applies at or above;
     * `"0"` (the default) applies at every quantity. Greatest qualifying
     * `min_qty` wins, exactly as §2.8's tier break does. */
    min_qty: SalesQtyOrZero,
    valid_from: SalesBusinessDate,
    valid_to: SalesBusinessDate.nullable(),
    reason: z.string(),
    status: OverrideStatus,
    review: CustomerPriceOverrideReview.optional(),
    created_by: Uuid,
    created_at: isoDateTime(),
    updated_at: isoDateTime(),
  })
  .openapi("CustomerPriceOverride");

/** §5 GET /v1/customers/{id}/price-overrides — the Customer 360 Pricing tab's
 * list. Cursor envelope like every other sales list, newest first. */
export const CustomerPriceOverrideListResponse = z
  .object({ items: z.array(CustomerPriceOverride), next_cursor: z.string().nullable() })
  .openapi("CustomerPriceOverrideListResponse");

const overrideCreateCommon = {
  customer_id: Uuid,
  product_id: Uuid,
  /** D-241 quantity scale in the product's BASE UoM; omitted = `"0"`. */
  min_qty: SalesQtyOrZero.optional(),
  valid_from: SalesBusinessDate,
  valid_to: SalesBusinessDate.optional(),
  /** §2.9: mandatory justification — the §4.6 card's "why". */
  reason: z.string().min(1).max(2000),
};

/**
 * §5 POST /v1/customer-price-overrides. A DISCRIMINATED UNION on
 * `override_kind`, so the §2.9 kind-pairing CHECK is the request's shape: a
 * `fixed_price` proposal carries `unit_price_minor` and cannot carry
 * `discount_pct`; a `discount_pct` proposal the reverse. A client cannot
 * send the pair the CHECK forbids, and the server never has to invent a
 * refusal for it.
 *
 * NO `currency` FIELD, deliberately. §2.9's `currency` is stamped from the
 * customer's `default_currency` at creation: the §3.2 engine resolves an
 * override only when its currency matches the document's (§9.13 — no silent
 * FX, ever), so any other value would author a row that never prices, and a
 * field with exactly one legal value is a field that exists to be got wrong.
 * §5's body ("customer_id, product_id, kind, value, validity, reason") does
 * not list it either. The stored currency comes back on the DTO.
 *
 * WHAT HAPPENS ON SUBMIT (§3.4, §7's "auto-routes to approval on breach"):
 * the service runs the guard in the frame `CustomerPriceOverrideReview`
 * describes. Inside the guard → `active` immediately and the §3.2 engine
 * prices from it on the next resolve. Breaching, or unprovable (no cost
 * basis — fails closed) → `pending_approval`, IGNORED by the engine until
 * decided, with `review` on the response. A proposal of EITHER kind for a
 * (customer, product) with no active list price on the customer's tier
 * answers 422 PRICE_NOT_LISTED — `fixed_price` no less than `discount_pct`:
 * §3.2 only reaches step 2 after step 1 found a list row, so the override
 * would sit `active` and never price, and a row nothing can resolve is dead
 * data authored under a reason.
 */
export const CustomerPriceOverrideCreateRequest = z
  .discriminatedUnion("override_kind", [
    z
      .object({
        override_kind: z.literal("fixed_price"),
        /** Per the product's BASE UoM (§2.9, D-237); the customer's currency. */
        unit_price_minor: SalesMinorMoneyNonNegative,
        ...overrideCreateCommon,
      })
      .strict(),
    z
      .object({
        override_kind: z.literal("discount_pct"),
        /** Applied to the resolved step-1 tier price (§2.9). */
        discount_pct: SalesPct,
        ...overrideCreateCommon,
      })
      .strict(),
  ])
  .superRefine((body, ctx) => {
    // §2.9's `customer_price_overrides_validity_ordered` CHECK, at the
    // contract so the refusal is a field-level 400 the form can point at
    // rather than a constraint violation surfacing from the INSERT.
    if (body.valid_to !== undefined && body.valid_to < body.valid_from) {
      ctx.addIssue({
        code: "custom",
        path: ["valid_to"],
        message: "valid_to must not precede valid_from (spec 09 §2.9)",
      });
    }
  })
  .openapi("CustomerPriceOverrideCreateRequest");

/** §5 POST /v1/customer-price-overrides/{id}/decide (D-231). `note` is
 * MANDATORY on `rejected` — enforced in the service as
 * 422 DECISION_NOTE_REQUIRED (a conditional requirement is not a zod object
 * shape without turning the DTO into a union the client would discriminate),
 * the same two-layer shape as `QuoteDecideRequest`. On rejection the note
 * becomes the row's `reason` (§5). */
export const CustomerPriceOverrideDecideRequest = z
  .object({
    outcome: z.enum(["approved", "rejected"]),
    note: z.string().max(2000).nullish(),
  })
  .strict()
  .openapi("CustomerPriceOverrideDecideRequest");

/** §5 POST /v1/customer-price-overrides/{id}/revoke — the reason is
 * mandatory: a revocation is an audited decision (§5), and one without a
 * reason is a row whose history cannot be read. */
export const CustomerPriceOverrideRevokeRequest = z
  .object({ reason: z.string().min(1).max(2000) })
  .strict()
  .openapi("CustomerPriceOverrideRevokeRequest");

// --- off-cut discount classes (§2.10) --------------------------------------

export const OffcutDiscountClass = z
  .object({
    id: Uuid,
    code: z.string().regex(/^[A-Z][A-Z0-9_]{1,47}$/),
    name_i18n: SalesLocaleName,
    // D-209: the wire keeps `name` — the row-local English projection.
    // Clients render `name_i18n` in the document/customer locale via the
    // shared localeText resolution.
    name: z.string(),
    min_pieces: z.number().int().min(1),
    max_pieces: z.number().int().min(1),
    min_piece_length: SalesQty.nullable(),
    max_piece_length: SalesQty.nullable(),
    // D-337 ruling 6: ABSENT without `sales.read_value`. A quote line serves
    // its `offcut_class_id` to every reader, and the class's rate beside the
    // product's list price would rebuild the line's price.
    discount_pct: SalesPct.optional(),
    bypasses_margin_guard: z.boolean(),
    status: CustomerStatus,
    created_at: isoDateTime(),
    updated_at: isoDateTime(),
  })
  .openapi("OffcutDiscountClass");

export const OffcutDiscountClassListResponse = z
  .object({ items: z.array(OffcutDiscountClass) })
  .openapi("OffcutDiscountClassListResponse");

export const OffcutDiscountClassCreateRequest = z
  .object({
    code: z.string().regex(/^[A-Z][A-Z0-9_]{1,47}$/),
    name_i18n: SalesLocaleName,
    min_pieces: z.number().int().min(1).optional(),
    max_pieces: z.number().int().min(1),
    min_piece_length: SalesQty.optional(),
    max_piece_length: SalesQty.optional(),
    discount_pct: SalesPct,
    bypasses_margin_guard: z.boolean().optional(),
  })
  .strict()
  .openapi("OffcutDiscountClassCreateRequest");

export const OffcutDiscountClassUpdateRequest = z
  .object({
    name_i18n: SalesLocaleName.optional(),
    min_pieces: z.number().int().min(1).optional(),
    max_pieces: z.number().int().min(1).optional(),
    min_piece_length: SalesQty.nullable().optional(),
    max_piece_length: SalesQty.nullable().optional(),
    discount_pct: SalesPct.optional(),
    bypasses_margin_guard: z.boolean().optional(),
    status: CustomerStatus.optional(),
  })
  .strict()
  .openapi("OffcutDiscountClassUpdateRequest");

// --- margin guards (§2.11) -------------------------------------------------

export const MarginGuard = z
  .object({
    id: Uuid,
    scope: MarginGuardScope,
    product_id: Uuid.nullable(),
    family_id: Uuid.nullable(),
    min_margin_pct: SalesPct,
    cost_basis: CostBasis,
    status: CustomerStatus,
    created_at: isoDateTime(),
    updated_at: isoDateTime(),
  })
  .openapi("MarginGuard");

export const MarginGuardListResponse = z
  .object({ items: z.array(MarginGuard) })
  .openapi("MarginGuardListResponse");

/** `cost_basis='moving_avg'` → 422 COST_BASIS_UNAVAILABLE until module 11
 * (P4; §2.11 stated degradation). `scope='family'` → 422
 * FAMILY_SCOPE_UNAVAILABLE: module 01's shipped schema has no
 * product_families table (P3-A decision; the §2.11 FK is undeclarable today). */
export const MarginGuardCreateRequest = z
  .object({
    scope: MarginGuardScope,
    product_id: Uuid.optional(),
    family_id: Uuid.optional(),
    min_margin_pct: SalesPct,
    cost_basis: CostBasis.optional(),
  })
  .strict()
  .openapi("MarginGuardCreateRequest");

export const MarginGuardUpdateRequest = z
  .object({
    min_margin_pct: SalesPct.optional(),
    cost_basis: CostBasis.optional(),
    status: CustomerStatus.optional(),
  })
  .strict()
  .openapi("MarginGuardUpdateRequest");

// --- sales settings (§2.22) ------------------------------------------------

const reminderCadenceOverride = z
  .object({
    ready_reminder_after_days: z.number().int().min(1).max(30).optional(),
    ready_reminder_max_sends: z.number().int().min(0).max(10).optional(),
    quote_follow_up_after_days: z.number().int().min(1).max(30).optional(),
    quote_follow_up_max_sends: z.number().int().min(0).max(5).optional(),
  })
  .strict();

/** §2.22 `reminder_tier_overrides` — zod-validated keys, an unknown key
 * answers 400 VALIDATION_FAILED (the strict body pipe; the column CHECK holds
 * only the object shape; THIS is the validator). */
export const ReminderTierOverrides = z
  .object({
    market: reminderCadenceOverride.optional(),
    b2b: reminderCadenceOverride.optional(),
    distributor: reminderCadenceOverride.optional(),
  })
  .strict()
  .openapi("ReminderTierOverrides");

const timeOfDay = z
  .string()
  .regex(/^([01]\d|2[0-3]):[0-5]\d$/)
  .openapi({ example: "21:00", description: "HH:MM, tenant HQ timezone (§2.22 quiet hours)" });

/**
 * §2.22's tenant-authored document text, one entry per document locale
 * (P3-D PR-D4, D-288 ruling 3). The three sales PDFs print the entry for the
 * CUSTOMER's locale and omit the block entirely when that locale has none —
 * never a placeholder, never another locale's text substituted silently, and
 * never invented bank details on a payment demand. Plain text; line breaks
 * are kept as written.
 */
export const DocumentTextBundle = z
  .object(localeKeys(z.string().min(1).max(2000).optional()))
  .strict()
  .openapi("DocumentTextBundle");

/** The §2.22 row. The reminder ENGINE reading the cadence fields is P3-E; the
 * settings surface ships now so a PUT never eats configuration the engine
 * will honour (stated in the migration; not a stub — GET/PUT are complete). */
export const SalesSettings = z
  .object({
    ready_reminder_after_days: z.number().int().min(1).max(30),
    ready_reminder_max_sends: z.number().int().min(0).max(10),
    quote_follow_up_after_days: z.number().int().min(1).max(30),
    quote_follow_up_max_sends: z.number().int().min(0).max(5),
    reminder_tier_overrides: ReminderTierOverrides,
    quiet_hours_start: timeOfDay,
    quiet_hours_end: timeOfDay,
    delivery_auto_confirm_days: z.number().int().min(1).max(60),
    tracking_show_prices: z.boolean(),
    delivery_note_show_prices: z.boolean(),
    /** The payment block the proforma prints (bank, account, reference
     * instructions) — D-288 ruling 3. Empty object on a tenant that wrote none. */
    proforma_payment_details_i18n: DocumentTextBundle,
    /** The letterhead text all three sales documents carry under the tenant's
     * name (legal name, address, registration numbers). */
    document_header_i18n: DocumentTextBundle,
    /** The footer line all three sales documents carry. */
    document_footer_i18n: DocumentTextBundle,
    updated_at: isoDateTime(),
    /**
     * When the sales engine last finished a sweep of this tenant (P3-E PR-E2,
     * D-292 ruling 11; migration 0067). The settings screen renders it as
     * "Engine last ran …", and that line is the deployed liveness proof of
     * the worker. NULL until the first sweep. READ-ONLY: the PUT neither
     * accepts nor changes it — only the engine writes it.
     */
    engine_swept_at: isoDateTime().nullable(),
    /**
     * The engine's ACTIVATION HORIZON (D-292 ruling 5): stamped once, by the
     * tenant's first sweep and BEFORE any reminder step is computed. The two
     * reminder sequences chase only documents whose anchor (`ready_at`,
     * `last_sent_at`) is at or after it, so go-live day never sends a backlog
     * of reminders. Expiry and auto-confirm are facts, not messages, and
     * apply to every document regardless. NULL until the first sweep.
     * READ-ONLY, exactly as `engine_swept_at` is.
     */
    reminders_active_since: isoDateTime().nullable(),
  })
  .openapi("SalesSettings");

/** §5 PUT — full-row payload (the whole point: no partial merge ambiguity).
 * The engine's two stamps (`engine_swept_at`, `reminders_active_since`) are
 * NOT fields here: `.strict()` answers 400 VALIDATION_FAILED for either (the
 * body pipe's `unrecognized_keys`), so no client can move the liveness proof
 * or the activation horizon (D-292, A7). */
export const SalesSettingsPutRequest = z
  .object({
    ready_reminder_after_days: z.number().int().min(1).max(30),
    ready_reminder_max_sends: z.number().int().min(0).max(10),
    quote_follow_up_after_days: z.number().int().min(1).max(30),
    quote_follow_up_max_sends: z.number().int().min(0).max(5),
    reminder_tier_overrides: ReminderTierOverrides.optional(),
    quiet_hours_start: timeOfDay,
    quiet_hours_end: timeOfDay,
    delivery_auto_confirm_days: z.number().int().min(1).max(60),
    tracking_show_prices: z.boolean(),
    delivery_note_show_prices: z.boolean(),
    /** Optional, as `reminder_tier_overrides` is: an absent bundle keeps the
     * stored one (a client written before PR-D4 must not wipe a tenant's
     * payment text by omission); `{}` clears it. */
    proforma_payment_details_i18n: DocumentTextBundle.optional(),
    document_header_i18n: DocumentTextBundle.optional(),
    document_footer_i18n: DocumentTextBundle.optional(),
  })
  .strict()
  .openapi("SalesSettingsPutRequest");

// --- pricing resolve (§3.2, §5 POST /v1/pricing/resolve) -------------------

/** §2.14's fulfilment-plan entries, declared here because the §3.2 dry-run
 * takes them as its `fulfillment_hint` and the quote slices reuse them. */
export const FulfillmentPlanStockEntry = z
  .object({
    kind: z.literal("stock_unit"),
    stock_unit_id: Uuid,
    qty: SalesQty,
  })
  .strict();
export const FulfillmentPlanOffcutEntry = z
  .object({
    kind: z.literal("offcut_combo"),
    pieces: z
      // `length` is module 06's measure (numeric(18,6)), never a sales
      // quantity — this door is fed straight from the fulfillment-options
      // response, and a narrower type here refused the API's own output.
      .array(z.object({ stock_unit_id: Uuid, length: SalesMeasure }).strict())
      .min(1)
      .max(4),
  })
  .strict();
export const FulfillmentPlanProductionEntry = z
  .object({
    kind: z.literal("production"),
    qty: SalesQty,
  })
  .strict();
export const FulfillmentPlanEntry = z
  .discriminatedUnion("kind", [
    FulfillmentPlanStockEntry,
    FulfillmentPlanOffcutEntry,
    FulfillmentPlanProductionEntry,
  ])
  .openapi("FulfillmentPlanEntry");

export const PricingResolveRequest = z
  .object({
    customer_id: Uuid,
    product_id: Uuid,
    qty: SalesQty,
    uom_id: Uuid,
    wanted_date: SalesBusinessDate,
    fulfillment_hint: z.array(FulfillmentPlanEntry).max(10).optional(),
  })
  .strict()
  .openapi("PricingResolveRequest");

/** §3.2's output. Margin/cost fields (`unit_cost_minor`, `margin_pct`,
 * `margin_guard_pct`) are OMITTED without `sales.margin.read` (§7);
 * `margin_breached` is always present — the boolean is what an unprivileged
 * composer renders as "needs approval" (§4.4). `distributor_fallback` is the
 * §3.2 step-1 special rule made a typed fact ("b2b (not in eligible list)"
 * chip, §9.5 — never an error). */
export const PricingResolveResponse = z
  .object({
    list_price_minor: SalesMinorMoneyNonNegative,
    unit_price_minor: SalesMinorMoneyNonNegative,
    currency: SalesCurrency,
    price_source: PriceSource,
    price_source_ref: Uuid.nullable(),
    offcut_class_id: Uuid.optional(),
    distributor_fallback: z.boolean(),
    unit_cost_minor: SalesMinorMoneyNonNegative.optional(),
    margin_pct: SalesSignedPct.optional(),
    margin_guard_pct: SalesPct.optional(),
    margin_breached: z.boolean(),
  })
  .openapi("PricingResolveResponse");

// --- sales activities (tasks) — P3-E PR-E2, D-292; migration 0067 ---------
//
// ACTIVITIES STEP 1 (D-292 ruling 1): the manager's task list. A task is
// either MANUAL (a person wrote it: `title` required, `created_by` set) or
// SYSTEM (the engine wrote it because a customer could not be chased any
// further). System tasks carry NO PROSE (ruling 3, D-225): `origin` +
// `reason_code` + the subject ids are the whole message, and the surface
// renders the words. A system task closes ITSELF when the state it chases
// ends (`status: cancelled`, `cancel_reason: PREMISE_ENDED` — ruling 4); a
// manual task never does.
//
// P3-E2 PR E2-1 (D-313; migration 0073) adds the rest of the task surface:
// one PATCH for a new day, a new assignee or new words (ruling 1), WITHDRAW
// for a manual task (`cancelled` with `cancel_reason: WITHDRAWN` and
// `cancelled_by`, ruling 2 — no row is ever deleted), the `due` filter
// (ruling 3), the typed `order` whose declared default is `worklist`
// (ruling 4), the deal as a subject and a filter (ruling 5), and the
// next-step summary a deal or a customer serves to every reader of it
// (`SalesNextActivity`, ruling 6). Tasks still emit no event (ruling 11).

/** `sales_activities.kind` CHECK verbatim (0067). */
export const SalesActivityKind = z.enum(["call", "meeting", "todo"]).openapi("SalesActivityKind");

/** `sales_activities.status` CHECK verbatim (0067). `cancelled` is reached
 * two ways since E2-1: the engine closes a system task whose premise ended,
 * and a person withdraws a manual task (D-313 ruling 2). `cancel_reason`
 * says which. */
export const SalesActivityStatus = z
  .enum(["open", "done", "cancelled"])
  .openapi("SalesActivityStatus");

/**
 * WHO wrote the task (0067's `origin` CHECK verbatim):
 *  * `manual` — a person, through `POST /v1/sales-activities`;
 *  * `reminder_cap_out` — a reminder sequence ended with the customer still
 *    not acting: the cap was reached, or a reminder could not be delivered
 *    (no channel, or the customer opted out). Written ONCE PER DOCUMENT EVER;
 *  * `push_no_channel` — a status push (or the received acknowledgement)
 *    could not be sent because the customer has no reachable channel. At
 *    most one OPEN per document.
 */
export const SalesActivityOrigin = z
  .enum(["manual", "reminder_cap_out", "push_no_channel"])
  .openapi("SalesActivityOrigin");

/**
 * WHY a system task exists — a stable code the surface localizes (D-225),
 * never server prose. NULL on every manual task (0067 CHECKs it):
 *  * `READY_NOT_COLLECTED` — the ready order's reminders reached their cap;
 *  * `QUOTE_NO_ANSWER` — the sent quote's follow-ups reached their cap;
 *  * `CUSTOMER_OPTED_OUT` — a reminder was skipped because the customer
 *    opted out of messages;
 *  * `NO_CHANNEL` — a message was skipped because the customer has no
 *    reachable messaging channel.
 */
export const SalesActivityReason = z
  .enum(["READY_NOT_COLLECTED", "QUOTE_NO_ANSWER", "CUSTOMER_OPTED_OUT", "NO_CHANNEL"])
  .openapi("SalesActivityReason");

/** How a `cancelled` task was closed (0073's CHECK verbatim, D-313 ruling 2):
 *  * `PREMISE_ENDED` — the engine closed a SYSTEM task because the state it
 *    chased ended (the order left `ready`, the quote left `sent`, the order
 *    was delivered or closed), or, since D-317, because the task's customer
 *    was archived. The row does not say which. `cancelled_by` is null;
 *  * `WITHDRAWN` — a person withdrew a MANUAL task through
 *    `POST /v1/sales-activities/{id}/withdraw`. `cancelled_by` names them.
 * A response enum that gains a value: a client switching on it renders an
 * unknown value as the automatic close it already knew. */
export const SalesActivityCancelReason = z
  .enum(["PREMISE_ENDED", "WITHDRAWN"])
  .openapi("SalesActivityCancelReason");

/**
 * A subject's NEXT STEP (spec 09 §3.12, D-313 ruling 6): the earliest open
 * task on a deal or a customer, whoever wrote it and whoever it is assigned
 * to. It carries the task's kind, its due date and two server-computed flags
 * against the tenant's business today, and NEVER the task's words or its
 * assignee. That is what lets it be served to every reader of the subject,
 * outside spec 09 §7's withheld fields, and what lets `needs_next_step`
 * filter on it under spec 16 §9.23. `null` on the subject means there is no
 * open task on it.
 */
export const SalesNextActivity = z
  .object({
    kind: SalesActivityKind,
    /** The tenant business date the earliest open task is due on. */
    due_on: SalesBusinessDate,
    /** `due_on` is before the tenant's business today; the flag shows red. */
    overdue: z.boolean(),
    /** `due_on` is the tenant's business today; the flag shows amber. */
    due_today: z.boolean(),
  })
  .openapi("SalesNextActivity");

/** `opportunities.status` CHECK verbatim (0073, spec 09 §2.25). Declared in
 * this base file so the quote detail's deal reference and the deal contract
 * (`./opportunity-schemas.ts`) share one vocabulary without an import cycle
 * between the quote and the deal files. */
export const OpportunityStatus = z.enum(["open", "won", "lost"]).openapi("OpportunityStatus");

/**
 * One `sales_activities` row as `/sales/tasks`, the order detail's and the
 * quote composer's open-task lines, the deal page, the peeks and My day read
 * it (spec 09 §5, D-292, D-313). The subject ids come with their display
 * labels so a row renders its links without a second read.
 *
 * A LABEL FOLLOWS ITS OWN DOOR (D-312 ruling 21, from E2-1). Each label —
 * `customer_name`, `quote_no`, `order_no`, and the deal's `opportunity_no`
 * with `opportunity_title` — is NULL when its id is NULL, and ALSO when the
 * door of that record would not answer this reader (the customer, quote,
 * order or deal door's own narrowing), while the id is still served. Until
 * E2-1 the three older labels were served whenever their id was set, and the
 * eligibility the create applies held only at assignment, so a customer
 * unassigned later left them open.
 */
export const SalesActivity = z
  .object({
    id: Uuid,
    kind: SalesActivityKind,
    /** The manual task's own words; NULL on every system task (ruling 3). */
    title: z.string().nullable(),
    note: z.string().nullable(),
    customer_id: Uuid.nullable(),
    /** NULL when `customer_id` is, or when the customer door would not
     * answer this reader (D-312 ruling 21). */
    customer_name: z.string().nullable(),
    quote_id: Uuid.nullable(),
    /** NULL when `quote_id` is, or when the quote door would not answer this
     * reader (D-312 ruling 21). */
    quote_no: z.string().nullable(),
    sales_order_id: Uuid.nullable(),
    /** NULL when `sales_order_id` is, or when the order door would not answer
     * this reader (D-312 ruling 21). */
    order_no: z.string().nullable(),
    /** The deal this task is on (D-313 ruling 5). A system task never names a
     * deal (0073's `sales_activities_system_no_opportunity`). */
    opportunity_id: Uuid.nullable(),
    /** The deal's `OPP-…` number and its title. Both NULL when
     * `opportunity_id` is, or when the deal door would not answer this reader
     * (§7 narrowing 15, D-312 ruling 21). */
    opportunity_no: z.string().nullable(),
    opportunity_title: z.string().nullable(),
    assignee_user_id: Uuid,
    /** NULL when the assignee's display name cannot be resolved (a removed
     * member): the id still names them. */
    assignee_name: z.string().nullable(),
    /** The tenant business date the task is due on. */
    due_on: SalesBusinessDate,
    /** SERVER-COMPUTED: `status = open` and `due_on` is before the tenant's
     * business today (its HQ timezone), so every reader agrees on red. */
    overdue: z.boolean(),
    status: SalesActivityStatus,
    origin: SalesActivityOrigin,
    reason_code: SalesActivityReason.nullable(),
    cancel_reason: SalesActivityCancelReason.nullable(),
    /** Set exactly when `status = done` (0067 CHECK), with `done_by`. */
    done_at: isoDateTime().nullable(),
    done_by: Uuid.nullable(),
    /** Set exactly when `status = cancelled` (0067 CHECK), with
     * `cancel_reason`. */
    cancelled_at: isoDateTime().nullable(),
    /** The person who withdrew a manual task (`cancel_reason: WITHDRAWN`);
     * NULL on every other row, a system task the engine closed included
     * (0073's `sales_activities_cancel_shape`, D-313 ruling 2). */
    cancelled_by: Uuid.nullable(),
    /** The person who wrote a manual task; NULL means the engine wrote it.
     * `created_by = assignee_user_id` on a manual row read under
     * `scope=mine` is how a client judges the withdraw author rule without
     * knowing its own user id (§7 narrowing 17). */
    created_by: Uuid.nullable(),
    created_at: isoDateTime(),
    updated_at: isoDateTime(),
  })
  .openapi("SalesActivity");

/**
 * `due` on `GET /v1/sales-activities` (D-313 ruling 3), each window computed
 * against the tenant's business today (its HQ timezone), so a stored view
 * means the same thing on every day:
 *  * `overdue` — `due_on` before today;
 *  * `today` — `due_on` is today;
 *  * `through_today` — `due_on` on or before today (My day);
 *  * `upcoming` — `due_on` after today and at most seven days ahead.
 * It narrows by date only, so pairing it with `status=done` or
 * `status=cancelled` narrows those rows by their due date.
 */
export const SalesActivityDue = z
  .enum(["overdue", "today", "through_today", "upcoming"])
  .openapi("SalesActivityDue");

/**
 * The typed order of `GET /v1/sales-activities` (D-313 ruling 4; `-` prefix =
 * descending). `worklist` is the DECLARED DEFAULT the server applies when the
 * parameter is absent, and it is the order PR-E2 shipped byte for byte: open
 * tasks by `due_on` then `created_at` ascending, done and cancelled ones by
 * `updated_at` newest first, ties by id. A single token keeps the saved-view
 * registry's single-token default (spec 16 §3.12). The other three sort every
 * status alike and break ties by id, so the keyset stays exact. The cursor
 * encodes the order it was minted under, and a cursor presented under a
 * different effective order answers 400 `invalid_cursor`.
 */
export const SalesActivitiesOrder = z
  .enum(["worklist", "due_on", "-due_on", "-updated_at"])
  .openapi("SalesActivitiesOrder");

/** `scope` of `GET /v1/sales-activities`: `mine` (default) is the tasks
 * assigned to the caller; `all` is the caller's whole visible set — every task
 * for group_admin and facility_admin, the tasks assigned to them or on their
 * assigned customers for a sales principal, and only their own for anyone
 * else. Never wider than that narrowing and never a 403 for a non-admin
 * (D-224 ruling 5: a filter is intersected, never widened). */
export const SalesActivityScope = z.enum(["mine", "all"]).openapi("SalesActivityScope");

/**
 * `GET /v1/sales-activities` query (spec 09 §5, D-292 ruling 2). Every filter
 * is optional and they AND together; a value outside its enum, or an id that
 * is not a uuid, is 400 VALIDATION_FAILED rather than an empty page. The
 * controller parses with THIS schema, so the contract and the door cannot
 * disagree about what a filter admits. `status` defaults to `open`.
 *
 * E2-1 (D-313 rulings 3 to 5) adds four, each optional so a PR-E2 request
 * reads exactly as before: `opportunity_id` (a deal filter the caller cannot
 * read answers an empty page), `assignee_user_id` (applied INSIDE narrowing
 * 10, so it can only narrow what the caller already reads, never widen it),
 * `due` (`SalesActivityDue`) and `order` (`SalesActivitiesOrder`, declared
 * default `worklist`). No existing parameter is tightened.
 */
export const SalesActivityListQuery = z.object({
  scope: SalesActivityScope.optional(),
  status: SalesActivityStatus.optional(),
  customer_id: z.guid().optional(),
  quote_id: z.guid().optional(),
  sales_order_id: z.guid().optional(),
  opportunity_id: z.guid().optional(),
  assignee_user_id: z.guid().optional(),
  due: SalesActivityDue.optional(),
  order: SalesActivitiesOrder.optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(200).optional(),
});

export const SalesActivityListResponse = z
  .object({ items: z.array(SalesActivity), next_cursor: z.string().nullable() })
  .openapi("SalesActivityListResponse");

// --- the customer as the book and the 360 read it (§4.1, §4.2) -------------

/**
 * A customer as the book and the 360 read it (P3-E2 PR E2-1, D-313 rulings 6
 * and 7): the `Customer` row plus its NEXT STEP. The write doors (create,
 * PATCH, archive) keep answering the bare `Customer`, because the next step
 * is a read of the tasks and not a field of the row.
 *
 * `next_activity` is `SalesNextActivity` (the earliest open task on the
 * customer, whoever wrote it, by kind and date only) or null.
 * `needs_next_step` is the discipline flag: true when the customer is ACTIVE,
 * has LIVE BUSINESS (spec 09 §3.1 item 4's predicate — an open quote, an open
 * order or, since E2-1, an open deal) and has no open task. Both are served to
 * every reader of the customer and never withheld (spec 09 §7, D-313 ruling
 * 6), which is what lets `needs_next_step=true` filter the book and a saved
 * view store it (spec 16 §9.23).
 *
 * SPREAD, NOT `Customer.extend(...)`. An extended registered schema is
 * published as `allOf: [$ref Customer, {…}]`, and oasdiff compares `allOf`
 * branches one at a time, so the list and the 360 read as having lost every
 * `Customer` property (45 ERR on the first `ci:contracts` run). Spreading the
 * shape publishes one flat object holding every `Customer` field plus the two
 * new ones, which is exactly the wire, and oasdiff sees two added fields. The
 * contracts test holds the key set to `Customer`'s plus these two.
 *
 * P3-E2 PR E2-2a1 (D-323 rulings 5 and 7) adds two more, by the same spread:
 * `tags`, every tag the customer carries as `CustomerTagRef` (`{id, name,
 * status}`, ordered by the lower-cased name and then the id), and
 * `has_live_business`, exactly the archive guard's predicate (spec 09 §3.1
 * item 4; `liveBusinessSql`), so a row bulk Archive would refuse carries its
 * reason before the press. Both are served to every reader of the customer.
 * The 360 serves the same object under `customer`, and its pinned note beside
 * it as `pinned_note` (`Customer360Response`).
 *
 * P3-E2 PR E2-2b (D-324 ruling 15, spec 09 §5's E2-2 row 20) adds the four
 * tombstone fields by the same spread: `merged_into_customer_id`,
 * `merged_into_display_name`, `merged_into_openable` and `merged_at`, each
 * null on a customer that was not merged. They ride the book and the 360 and
 * never the bare `Customer` the write doors answer. The restore refuses a
 * merged customer 409 `CUSTOMER_MERGED` and the PATCH refuses it 422
 * `CUSTOMER_ARCHIVED`. The archive answers an archived customer 200 with the
 * bare row and writes nothing, so a merged one reads there as archived and the
 * tombstone is read from the 360.
 */
export const CustomerWithNextStep = z
  .object({
    ...Customer.shape,
    // Reads are never replays, so the alias is always present here (D-339).
    notes: CustomerNotesAlias.openapi({ description: CUSTOMER_NOTES_ALIAS_DESCRIPTION }),
    next_activity: SalesNextActivity.nullable(),
    needs_next_step: z.boolean(),
    tags: z.array(CustomerTagRef).openapi({
      description:
        "Every tag the customer carries, archived tags included with their status, ordered by the lower-cased name and then the id (spec 09 §5's E2-2 items).",
    }),
    has_live_business: z.boolean().openapi({
      description:
        "True while the customer has an open quote, an open order or an open deal: the archive guard's own predicate, so `POST /v1/customers/{id}/archive` answers 409 `CUSTOMER_HAS_OPEN_DOCS` exactly when this is true (spec 09 §3.1 item 4). Served to every reader.",
    }),
    merged_into_customer_id: Uuid.nullable().openapi({
      description:
        "The customer this one was merged into, or null for a customer that was never merged (spec 09 §2.1, §5's E2-2 row 20). Always one hop, because a later merge that takes the winner away re-points this pointer at the new winner (§3.13). A merged customer is archived, and its 360 still answers as the tombstone.",
    }),
    merged_into_display_name: z.string().nullable().openapi({
      description:
        "The winner's current `display_name`, joined at read time, so a tombstone a later merge re-pointed names the new winner. Every reader of a customer document is already served a customer's display name (#272), so the name leaks nothing. Null when the customer was not merged.",
    }),
    merged_into_openable: z.boolean().nullable().openapi({
      description:
        "True when the caller's customer door answers the winner, computed with the door's own arm (for a sales principal, an assignment on the winner), so the tombstone links to the winner only for a caller who can open it. It tells the caller only what its own door already answers. Null when the customer was not merged.",
    }),
    merged_at: isoDateTime().nullable().openapi({
      description: "When the merge committed. Null when the customer was not merged.",
    }),
  })
  .openapi("CustomerWithNextStep");

export const CustomerListResponse = z
  .object({ items: z.array(CustomerWithNextStep), next_cursor: z.string().nullable() })
  .openapi("CustomerListResponse");

/** §5 GET /v1/customers/{id} — the 360 payload. Its customer carries the next
 * step since E2-1 (`CustomerWithNextStep`), and its tags and live-business
 * flag since E2-2a1. `pinned_note` (E2-2a1, D-323 ruling 2) is the pinned
 * `CustomerNote` or null, beside the customer rather than on it, so the book's
 * items carry no note and the write doors' bare `Customer` carries none either
 * (spec 09 §5's E2-2 row 20). A redacted pinned note is served with its
 * marker body, and the customer's `notes` alias reads null for it. */
export const Customer360Response = z
  .object({
    customer: CustomerWithNextStep,
    pinned_note: CustomerNote.nullable(),
    contacts: z.array(CustomerContact),
    channels: z.array(ContactChannel),
    assignments: z.array(CustomerAssignment),
    latest_reliability: CustomerReliabilitySnapshot.nullable(),
    distributor_products: z.array(CustomerDistributorProduct),
    open_ar: OpenArState,
  })
  .openapi("Customer360Response");

/**
 * `POST /v1/sales-activities` — a MANUAL task (D-292 ruling 2). `title` is
 * trimmed and 1–200 characters; `note` at most 2000. `assignee_user_id`
 * absent means the caller. A sales principal may create only for themselves;
 * an admin (the literal group_admin or facility_admin role) may assign an
 * active member who holds `sales.activity.write` and can read the subject.
 * This text said `sales.read` until E2-1; the service has always checked
 * `sales.activity.write`, and D-313 ruling 10 corrects the text. Every
 * subject id is optional, a named subject must be one the caller can read,
 * and the named subjects must agree in the order customer, quote, order,
 * deal: the customer is derived from the quote, the order or the deal when
 * `customer_id` is absent, and a subject of another customer (an order that
 * is not the named quote's, or a quote linked to another deal) is 400
 * `subject_mismatch`. `opportunity_id` arrives with E2-1 (D-313 ruling 5).
 */
export const SalesActivityCreateRequest = z
  .object({
    kind: SalesActivityKind,
    title: z.string().trim().min(1).max(200),
    note: z.string().max(2000).nullish(),
    customer_id: Uuid.optional(),
    quote_id: Uuid.optional(),
    sales_order_id: Uuid.optional(),
    opportunity_id: Uuid.optional(),
    assignee_user_id: Uuid.optional(),
    due_on: SalesBusinessDate,
  })
  .strict()
  .openapi("SalesActivityCreateRequest");

/**
 * `PATCH /v1/sales-activities/{id}` — move a task to another day, reassign it
 * or edit its words (spec 09 §3.12, D-313 ruling 1), one request and one audit
 * row. At least one key is required, and the body is strict.
 *  * `due_on` — the assignee or a literal admin (§7 narrowing 11 as extended);
 *    a past date is allowed, and the task is then overdue at once;
 *  * `assignee_user_id` — a literal admin only, onto an assignee who could
 *    have been named at create (an active member holding
 *    `sales.activity.write` who can read the subject), else 422
 *    `ACTIVITY_ASSIGNEE_NOT_ELIGIBLE`;
 *  * `kind`, `title`, `note` — a MANUAL task's words only; sending any of
 *    them for a system task answers 400 `VALIDATION_FAILED` with the reason
 *    `system_task_words`. `title` keeps the create's rule (trimmed, 1–200);
 *    `note: null` clears the note.
 * The audit row names the fields that changed and carries `from` and `to`
 * only for `due_on` and `assignee_user_id`, so no title or note ever reaches
 * the chain (spec 09 §7's payload table).
 */
export const SalesActivityUpdateRequest = z
  .object({
    due_on: SalesBusinessDate.optional(),
    assignee_user_id: Uuid.optional(),
    kind: SalesActivityKind.optional(),
    title: z.string().trim().min(1).max(200).optional(),
    note: z.string().max(2000).nullable().optional(),
  })
  .strict()
  .refine((patch) => Object.values(patch).some((value) => value !== undefined), {
    message: "at least one of due_on, assignee_user_id, kind, title or note is required",
    params: { reason: "empty_patch" },
  })
  .openapi("SalesActivityUpdateRequest");

// --- problem codes (RFC 9457 `code`, stable UPPER_SNAKE — §5) --------------

export const salesProblemCodes = [
  // customers + contacts + channels
  "CUSTOMER_NOT_FOUND",
  "CUSTOMER_ARCHIVED", // archived customers block new documents (§3.1.4), and a deal's reopen (D-312 ruling 9). Since E2-2b (§5's E2-2 rows 22 and 22b) the problem carries `extensions.merged_into_customer_id` naming the winner when the customer was merged, built by `customerArchivedProblem(row)` at every door
  "CUSTOMER_HAS_OPEN_DOCS", // §5 archive: open quotes/orders exist
  "CONTACT_NOT_FOUND",
  "CHANNEL_NOT_FOUND",
  "IDENTITY_TAKEN", // §2.4 UNIQUE; 409 carries owning_customer_id for an address-grant holder only (§9.12, D-307)
  "IDENTITY_INVALID", // §2.4 normalization failed (not E.164 / not an email / …)
  "IDENTITY_NOT_FOUND", // resolve-identity miss (row-or-nothing, §9.15)
  "OWNER_NOT_MEMBER", // owner_user_id names no active member of this tenant
  // distributor list
  "NOT_DISTRIBUTOR", // §2.5: rows only for tier='distributor' customers
  "DISTRIBUTOR_PRODUCT_NOT_FOUND",
  "DISTRIBUTOR_PRODUCT_EXISTS", // §5: 409 on a duplicate eligible-list add (§2.5 UNIQUE)
  // price lists
  "PRICE_LIST_NOT_FOUND",
  "PRICE_LIST_CODE_TAKEN", // §2.7 UNIQUE (tenant_id, code)
  "PRICE_LIST_NOT_DRAFT", // item upsert / activation preconditions
  "VALIDITY_OVERLAP", // §5 activate: 409 with conflicting_price_list_id extension
  // guards + classes
  "MARGIN_GUARD_NOT_FOUND",
  "MARGIN_GUARD_EXISTS", // the §2.11 partial-unique surfaced as a 409
  "COST_BASIS_UNAVAILABLE", // moving_avg before module 11 (§2.11, stated)
  "FAMILY_SCOPE_UNAVAILABLE", // no product_families table in module 01 (P3-A decision)
  "OFFCUT_CLASS_NOT_FOUND",
  "OFFCUT_CLASS_CODE_TAKEN",
  // pricing resolve
  "PRICE_NOT_LISTED", // §3.2 step 1: no list row for (tier, currency, date, qty)
  // --- slice P3-C-1: the quote core (§2.12–§2.17, §3.4/§3.5, §4.10) --------
  // ONE catalog per module, not one per slice: the module-catalog parity gate
  // holds THIS array against every code modules/sales raises, both ways, so a
  // second array would be a second place for a code to hide.
  // quote lifecycle (§3.4)
  "QUOTE_NOT_FOUND",
  "QUOTE_TERMINAL", // revise/cancel/reject on converted / rejected / cancelled
  // §3.4 send, and DELIBERATELY NOT QUOTE_TERMINAL (D-268, 2026-09-10): an
  // expired quote is not closed, it is re-opened by revision n+1 repriced at
  // current lists, and the refusal has to name that remedy. Sending one at its
  // stale pinned prices re-opened a 14-day window over a fortnight-old price
  // and skipped the §3.4 re-review, which is the defect this code exists to
  // refuse. Raised by QuotesService.send.
  "QUOTE_EXPIRED",
  // §9.13 / D-259: the customer's default_currency was PATCHed after this quote
  // was raised. A revise prices against the customer's current lists and stamps
  // the head's currency, so the two would disagree; duplicate re-prices into the
  // current currency instead. Never an FX conversion — module 11 owns FX.
  "QUOTE_CURRENCY_STALE",
  // P3-K K-3 (D-554 ruling 5): an agent's quote send names the revision its
  // verdict and its approval card judged; a revise that committed since is
  // refused 409, never sent unjudged. Raised by QuotesService.send for an
  // agent binding only, so no /v1 route answers it.
  "QUOTE_REVISION_CHANGED",
  "QUOTE_NOT_PENDING", // decide against a quote not in pending_approval
  "QUOTE_NOT_SENT", // remind / expire against a quote not in `sent`
  "QUOTE_NOT_ACCEPTABLE", // accept-manual against a non-sent quote
  "QUOTE_NO_LINES",
  // P3-J J-2 (D-465 ruling 5): a line names a promise that is not module 03's
  // quoted, unexpired promise for its product and quantity; `extensions.reason`
  // says which (`not_found`, `not_quoted`, `expired`, `product_mismatch`,
  // `qty_mismatch`) and `extensions.line_no` names the line.
  "QUOTE_LINE_PROMISE_INVALID",
  "REMINDER_TOO_SOON", // §5 remind: the 72 h rate limit
  "DECISION_NOTE_REQUIRED", // decide/credit-decision: a refusal needs a reason
  "APPROVAL_THRESHOLD_EXCEEDED", // §3.4: facility_admin above the 2pp / value bar
  "CONTACT_NOT_ON_CUSTOMER", // contact_id belongs to another customer
  "NO_SEND_CHANNEL", // §3.4 send: the customer has no messaging identity
  // document composition (D-234)
  "VAT_MODE_MIXED", // lines resolved from lists with different vat_mode
  // assignments (the D-241 endpoints)
  "ASSIGNMENT_NOT_FOUND",
  "ASSIGNMENT_EXISTS",
  "CANNOT_REMOVE_LAST_OWNER", // the guard §4.2's panel discloses before the press
  // --- slice P3-C-2: the §2.9 override surface (D-263, 2026-09-08) ---------
  // These left this catalog on 2026-09-01 (D-255) because no route could
  // raise them, and RETURN here WITH the four routes `register.ts` now mounts
  // — D-255 ruling 1's pairing rule, kept in both directions by the parity
  // gate. The first two are the names §5 forecast; the third is the honest
  // refusal the revoke row needs, in the module's own idiom (QUOTE_TERMINAL /
  // ORDER_TERMINAL): §2.9's status set is pending_approval / active /
  // expired / revoked, and `status → revoked` (§5) is a transition FROM the
  // two live states only. Revoking an `expired` row would rewrite a window
  // that already closed; revoking a `revoked` row a second time with a
  // different reason would silently discard the second reason — both are
  // 409, never a no-op that hides a mistake.
  "OVERRIDE_NOT_FOUND",
  "OVERRIDE_NOT_PENDING", // decide against an override not in pending_approval
  "OVERRIDE_TERMINAL", // revoke against an expired / already-revoked override
  // orders (§2.16, §3.6)
  "ORDER_NOT_FOUND",
  "ORDER_TERMINAL",
  "ORDER_NOT_BLOCKED", // credit-decision against a non-blocked order
  "ORDER_HAS_ACTIVE_WOS", // §5 cancel guard (module 04 holds live work orders)
  // P3-D-1 (D-283) — the direct order's two refusals, landing WITH
  // `POST /v1/orders` and `POST /v1/orders/{id}/decide` (D-255 ruling 1's
  // pairing rule; the module-catalog parity gate holds both against their
  // raisers).
  "ORDER_NOT_PENDING", // decide against an order not in pending_approval (the QUOTE_NOT_PENDING / OVERRIDE_NOT_PENDING idiom)
  "FACILITY_NOT_ELIGIBLE", // POST /v1/orders: a supplied fulfilling_facility_id that is not an ACTIVE facility of this tenant, or a plan naming stock at a facility outside the caller's eligible pool — FACILITY_UNRESOLVED keeps its one meaning (§2.16)
  "PROMISE_DATE_IN_PAST", // POST /v1/orders: a line's typed promise_date before the tenant's business today; `extensions.line_no` names the first such line (D-349 ruling 1)
  // P3-D PR-D2 (D-285) — the LINE cancel's five refusals, landing WITH
  // `POST /v1/orders/{id}/lines/{lineId}/cancel` (D-255 ruling 1's pairing
  // rule; the module-catalog parity gate holds each against its raise site in
  // OrdersService.cancelLine, BOTH WAYS). Precedence under the header lock,
  // after the 404s: ORDER_TERMINAL → ORDER_NOT_CONFIRMED → ORDER_LINE_TERMINAL
  // → LINE_HAS_ACTIVE_WOS → ORDER_LINE_PARTIALLY_SHIPPED. Two of the five
  // CANNOT FIRE in P3 and say so — they are catalogued because a raise site
  // exists, never as a reserved name.
  "ORDER_NOT_CONFIRMED", // line cancel on a `pending_approval` order — refused for EVERY principal, admins included (D-285 ruling 2): a pending order holds no reservation and module 03 was never told about its demand, so the release list would be empty and the event would describe nothing; the remedy is the admin's reject or the whole-order cancel (§9.19). The ORDER_NOT_PENDING / QUOTE_NOT_SENT idiom, checked AFTER ORDER_TERMINAL
  "ORDER_LINE_NOT_FOUND", // `lineId` is not one of `{id}`'s lines — module 06's TRANSFER_LINE_NOT_FOUND document-prefixed idiom. A line of ANOTHER order answers this same 404, never a 403 that confirms it exists (§9.15; `requireOrder`'s own rule)
  "LINE_HAS_ACTIVE_WOS", // §5's own name, kept verbatim — the line-level mirror of ORDER_HAS_ACTIVE_WOS: the line is `in_production`, i.e. at least one work order pegged to it is released or in progress. LIVE since P3-D PR-D6 (D-290): module 04 accepts `sales_order_line_id` on WO create and its release, completion and cancellation write the line through module 09's peg port, inside module 04's own transaction
  "ORDER_LINE_TERMINAL", // the line is already `shipped` or `cancelled` — the module's *_TERMINAL idiom (QUOTE_TERMINAL / OVERRIDE_TERMINAL / ORDER_TERMINAL). A work-order peg (create and release, PR-D6, D-290 ruling 6) admits only an `open` or `in_production` line, so it answers this for a `ready` line too
  "ORDER_LINE_PARTIALLY_SHIPPED", // an `open`/`ready` line with `qty_shipped > 0` — its OWN name and NOT ORDER_LINE_TERMINAL (D-285 ruling 4): a half-shipped line is half-OPEN, and calling it terminal would tell a client the line is finished when it is not; §9.18 rules a partial-line cancel out of v1, and the name states that remedy. LIVE since PR-D3 (D-286): `OrdersRepo.settleShipped` is `qty_shipped`'s first writer
  // P3-D PR-D3 (D-286) — the SHIPMENT surface's FIFTEEN refusals, landing WITH
  // the ten routes `quote-register.ts` mounts for it (D-255 ruling 1's pairing
  // rule; the module-catalog parity gate holds each against its raise site in
  // modules/sales, BOTH WAYS). Reused rather than re-minted: ORDER_NOT_FOUND,
  // ORDER_TERMINAL, ORDER_LINE_NOT_FOUND, ORDER_LINE_TERMINAL,
  // LINE_HAS_ACTIVE_WOS (above), FACILITY_NOT_ELIGIBLE (above) and the
  // platform's PERMISSION_DENIED. Precedence per door, in the order the
  // service meets them (the register's descriptions print the same order):
  //   open   — 404 ORDER_NOT_FOUND → 422 FACILITY_NOT_ELIGIBLE → 403 → under
  //            the header lock 409 ORDER_TERMINAL → ORDER_NOT_SHIPPABLE →
  //            CREDIT_BLOCKED → SHIPMENT_OPEN
  //   pick   — 403 (override lineage) → 404 SHIPMENT_NOT_FOUND → 409
  //            SHIPMENT_NOT_PICKING → 404 ORDER_LINE_NOT_FOUND → 409
  //            ORDER_LINE_TERMINAL → LINE_HAS_ACTIVE_WOS → 422
  //            QTY_EXCEEDS_REMAINING (line) → 409 UNIT_NOT_RESERVED →
  //            UNIT_NOT_SHIPPABLE → 422 QTY_EXCEEDS_REMAINING (unit) → 409
  //            UNIT_ALREADY_PICKED
  //   unpick — 404 SHIPMENT_NOT_FOUND → 409 SHIPMENT_NOT_PICKING → 404
  //            SHIPMENT_LINE_NOT_FOUND
  //   pack   — 404 → 409 SHIPMENT_NOT_PICKING → SHIPMENT_EMPTY
  //   ship   — 404 → 409 SHIPMENT_NOT_PACKED → CREDIT_BLOCKED
  //   cancel — 403 → 404 → 409 SHIPMENT_TERMINAL
  //   deliver / confirm-received — 409 ORDER_TERMINAL → ORDER_NOT_SHIPPED
  //   order + line cancel — 409 ORDER_HAS_SHIPMENTS
  "CREDIT_BLOCKED", // §2.16's fulfilment hold, LANDED (it was DELIBERATELY ABSENT from 2026-09-01, D-255, because its only raisers were P3-D's): raised by ShipmentsService.open when `credit_check_status` is `blocked_*`, and RE-CHECKED under the header lock by ShipmentsService.ship — a hold placed between open and ship stops the goods and the picks wait on the open shipment. Module 04's release of a work order pegged to one of the order's lines is the §2.16 second raiser, LANDED in P3-D PR-D6 (D-290 ruling 7): module 09's peg port answers it synchronously, before any component hold, and again under the header lock
  "ORDER_NOT_SHIPPABLE", // open against an order whose status is outside SHIPPABLE = {confirmed, in_production, ready} (D-286 ruling 2) — a `pending_approval` order holds nothing to pick; checked AFTER ORDER_TERMINAL, the ORDER_NOT_CONFIRMED idiom
  "SHIPMENT_OPEN", // open while a `picking` / `packed` shipment already exists on the order — ONE open shipment per order is migration 0063's partial UNIQUE (ruling 3), so partial shipments post sequentially; `extensions.shipment_id` + `shipment_no` name the open one
  "SHIPMENT_NOT_FOUND", // `{id}` is not a shipment this session may see — another tenant's, or one outside a facility-scoped caller's fence (the RESTRICTIVE policy), never a 403 that confirms it exists (§9.15)
  "SHIPMENT_NOT_PICKING", // pick / undo / pack against a shipment that is not `picking` — the lines freeze at pack (§3.7 step 4); the *_NOT_PENDING idiom
  "SHIPMENT_NOT_PACKED", // ship against a shipment that is not `packed` — only a packed shipment ships (§3.7 step 4's order)
  "SHIPMENT_EMPTY", // pack with no LIVE line (every pick undone, or none made) — a packed empty box is not a shipment
  "SHIPMENT_TERMINAL", // cancel against a `shipped` / `delivered` / `cancelled` shipment — the module's *_TERMINAL idiom; shipped goods come back only through E2-3's return chain
  "SHIPMENT_LINE_NOT_FOUND", // `lineId` is not a LIVE line of `{id}` — an already-undone line included; the ORDER_LINE_NOT_FOUND document-prefixed idiom
  "UNIT_NOT_RESERVED", // the scanned unit carries no active hold for THIS line: `extensions.reason` ∈ `not_reserved` (overridable by a facility_admin / group_admin lineage with `override: true`) | `reserved_for_other` (REFUSED whatever the flag — `reserved_for_ref` names the holder; the remedy is that order's line cancel, D-286 ruling 7). §5's own name
  "UNIT_NOT_SHIPPABLE", // the unit cannot leave from where it stands: `extensions.reason` ∈ `location_kind` (a `shipment` movement is legal from `bin` / `shipping` only — put it away first) | `unit_status` (not `filled` / `in_use`, or no measured contents) | `wrong_product` (a healthy unit of a different product from the line's — D-286 ruling 30) | `lot_not_released` | `other_facility`; raised from module 06's seam OUTCOME so 06's 422 MOVEMENT_ILLEGAL never escapes a route that did not declare it (ruling 8) | `no_lot` (a lotless unit — 0008 admits one, §2.19's line does not; refused by the seam before any movement)
  "QTY_EXCEEDS_REMAINING", // §5's own name — `extensions.remaining_of` ∈ `line` (the line's open quantity, checked BEFORE the seam) | `unit` (the unit's remaining measure, from the seam's `insufficient` outcome), with `remaining` and `requested`; 0055's `qty_shipped <= qty_ordered` CHECK is the floor under it
  "UNIT_ALREADY_PICKED", // a second LIVE pick from one unit for one line on one shipment — migration 0063's partial UNIQUE (undone rows excluded); the remedy is undo + re-pick the total
  "ORDER_HAS_SHIPMENTS", // ONE code, TWO predicates (ruling 11): the whole-order cancel (both doors, by construction) while any shipment of the order is not `cancelled` OR any line has shipped quantity — the belt that keeps `markCancelled` from taking a half-shipped `open` line to `cancelled`; EXCEPT an EMPTY open shipment (`picking`, no live pick — ruling 23: it holds nothing, refuses nothing, and is closed with the order); and the LINE cancel while a `picking` / `packed` shipment HOLDING PICKS exists on the order (its picks already sit on the ledger). A `shipped` sibling does not block a line cancel — that is D-285 ruling 11's hand-off
  "ORDER_NOT_SHIPPED", // mark-delivered / confirm-received against an order that is not `shipped` — a second office press, or a forged POST on a page that never offered the button (the customer's replay on a `delivered` order is 200, not this — ruling 14); checked AFTER ORDER_TERMINAL
  // P3-D PR-D4 (D-288, 2026-09-17) — the documents and the status override.
  // SIX new codes, each beside its raise site (the both-ways parity gate).
  // Reused rather than re-minted: ORDER_NOT_FOUND, ORDER_TERMINAL,
  // ORDER_NOT_CONFIRMED, QUOTE_NOT_FOUND, SHIPMENT_NOT_FOUND,
  // SHIPMENT_NOT_PACKED, SHIPMENT_OPEN, the three public GONE codes and the
  // platform's PERMISSION_DENIED. Precedence per door, in the order the
  // service meets them (the register's descriptions print the same order):
  //   override-status — 404 ORDER_NOT_FOUND → 403 (facility fence, before any
  //            guard read — D-286 ruling 25) → under the header lock 409
  //            ORDER_TERMINAL (also `cancelled` from shipped or delivered —
  //            the cancel door's own answer) → 422 STATUS_REGRESSION →
  //            STATUS_OVERRIDE_NOT_ALLOWED → 409 SHIPMENT_OPEN (→ shipped) →
  //            409 ORDER_HAS_ACTIVE_WOS (→ shipped, reused — the whole-order
  //            cancel's guard, D-288 ruling 21) → 422 ORDER_NOTHING_SHIPPED
  //            (→ shipped)
  //   quote pdf (office) — 404 QUOTE_NOT_FOUND → 403 (`sales.read_value`) →
  //            409 QUOTE_PDF_NOT_AVAILABLE
  //   quote pdf (token) — 404 TOKEN_INVALID → the GONE codes exactly as the
  //            public quote read answers them → 409 QUOTE_PDF_NOT_AVAILABLE
  //   proforma — 404 ORDER_NOT_FOUND → 403 (`sales.read_value`) → 409
  //            ORDER_NOT_CONFIRMED (pending_approval) → ORDER_TERMINAL
  //            (cancelled) → 422 DOCUMENT_GLYPH_COVERAGE
  //   delivery note — 404 SHIPMENT_NOT_FOUND → 409 SHIPMENT_NOT_PACKED →
  //            DELIVERY_NOTE_NOT_RENDERED
  //   delivery note re-render — 404 SHIPMENT_NOT_FOUND (the 0063 fence) →
  //            403 (write scope) → 409 SHIPMENT_NOT_PACKED → 422
  //            DOCUMENT_GLYPH_COVERAGE → 503 DEPENDENCY_UNAVAILABLE
  "STATUS_REGRESSION", // override-status to a target equal to or BEHIND the order's current status on §3.6's order (pending_approval < confirmed < in_production < ready < shipped < delivered < closed) — §5's own name; an order only moves forward (D-288 ruling 5). Raised by OrdersService.overrideStatus
  "STATUS_OVERRIDE_NOT_ALLOWED", // override-status to a FORWARD target this door does not own: `extensions.door` names the route that does (`mark-delivered` for delivered and for shipped → closed, `cancel` for cancelled from pending_approval through ready (from shipped or delivered the answer is the cancel door's own 409 ORDER_TERMINAL), `module_04` for in_production, `decide` for anything from pending_approval) — D-288 rulings 5 and 8. Raised by OrdersService.overrideStatus
  "ORDER_NOTHING_SHIPPED", // override-status → shipped on an order where no line carries shipped quantity: forcing it would let mark-delivered close an order whose customer received nothing; the remedy is the cancel (D-288 ruling 18). Raised by OrdersService.overrideStatus
  "QUOTE_PDF_NOT_AVAILABLE", // the office and token quote-PDF doors when the send in question has no PDF — a quote never sent, or a token minted before migration 0064 gave the send its `pdf_file_id` (D-288 rulings 1, 4). Raised by QuotesService.quotePdf and the public quote PDF door
  "DELIVERY_NOTE_NOT_RENDERED", // the delivery-note door on a packed shipment whose note could not be drawn at pack (the pack stood; ruling 11) — the remedy is `POST /v1/shipments/{id}/delivery-note`. Raised by ShipmentsService.deliveryNotePdf
  "ORDER_LINE_PRODUCT_MISMATCH", // a work order pegged to an order line (module 04's `POST /v1/work-orders` with `sales_order_line_id`, P3-D PR-D6, D-290 ruling 6) makes a different product than the line sells; the peg is refused. Raised by module 09's peg port inside module 04's create transaction
  "DOCUMENT_GLYPH_COVERAGE", // a document holds characters the bundled fonts cannot draw; `extensions.code_points` lists every one, the whole document checked before drawing, as `"U+XXXX"` strings (at least four hex digits, uppercase — e.g. `"U+0915"`, `"U+1F600"`) — printing tofu is refused, never degraded. Raised by DocumentsService (quote send, proforma, delivery-note re-render) and by SalesSettingsService on PUT, which refuses tenant document text the fonts cannot draw before it can stop every send in that language; at pack it is caught and recorded instead (D-288 rulings 9, 11)
  // §2.16's `fulfilling_facility_id` is DERIVED at acceptance (the units the
  // pinned plan names, else the tenant's oldest facility — D-254). A tenant
  // with NO facility cannot hold stock and cannot have quoted, so acceptance
  // refuses rather than inventing a warehouse for a real order to ship from.
  // Unreachable through provisioning, which is exactly why it is a NAMED code
  // and not an unmapped 500: the one path that could produce it is a tenant
  // whose facilities were removed under a live quote.
  "FACILITY_UNRESOLVED",
  // the public doors (§4.10) — deliberately COARSE on the not-found edge: a
  // public refusal never distinguishes "wrong token" from "no such tenant",
  // so the door cannot be used to probe which quote numbers exist. The three
  // GONE codes are the opposite case and say so on purpose: their holder has
  // a REAL token for their OWN document and is entitled to know which of the
  // three states it reached.
  "TOKEN_INVALID",
  "TOKEN_EXPIRED",
  "TOKEN_USED",
  "TOKEN_SUPERSEDED", // a newer revision was sent; this link is not the offer
  // `TOKEN_REVOKED` IS DELIBERATELY ABSENT (D-255, 2026-09-01). It was the one
  // deletion here that left a name the accept and decline route descriptions
  // went on printing; those were corrected on 2026-09-10 and now name the
  // three GONE codes listed just above, so the register no longer advertises a
  // refusal the product cannot give.
  // The reason for the deletion is that the accept door decided — with a unit
  // test pinning each arm — that a customer never meets a state called
  // "revoked":
  //   * a token revoked because a NEWER REVISION was sent renders
  //     `superseded` ("a newer offer was sent, here is who to ask"), which is
  //     the §4.10 page that state has;
  //   * a token revoked by `POST /v1/quotes/{id}/expire` renders `expired`
  //     (the force-expire flips the status AND revokes in one breath, and
  //     telling that customer a newer offer exists would send them looking for
  //     a message nobody sent);
  //   * a token revoked by a CANCEL or a DECLINE answers the generic 404 — the
  //     tenant withdrew the offer, and naming the state would tell a token
  //     holder about a document that no longer exists.
  // Every revocation therefore reaches the customer as one of three OTHER
  // codes, all catalogued and all raised. Keeping the name would catalogue a
  // refusal the product cannot give — the same defect as the three above, in
  // the same slice. It returns if a future state ever needs it.

  // sales activities — P3-E PR-E2 (D-292; migration 0067), each raised by
  // ActivitiesService (apps/api/src/modules/sales/activities/); E2-1 (D-313)
  // adds the PATCH and withdraw doors as raisers and one code:
  "ACTIVITY_NOT_FOUND", // 404: no such task, another tenant's, or one outside the caller's read narrowing (D-292 ruling 2) — never a different error for "exists but not yours" (§9.15's row-or-nothing rule). Raised by the complete, PATCH and withdraw doors
  "ACTIVITY_NOT_OPEN", // 409: complete, PATCH or withdraw on a task that is already `done`, or `cancelled` (its premise ended, or it was withdrawn). Raised by the complete, PATCH and withdraw doors
  "ACTIVITY_ASSIGNEE_NOT_ELIGIBLE", // 422: create or PATCH naming an assignee the caller may not assign — a non-admin naming anyone but themselves, or an admin naming someone who is not an active member holding `sales.activity.write` who can read the subject (this comment said `sales.read` until E2-1; the service checked `sales.activity.write` throughout, D-313 ruling 10). Raised by the create and PATCH doors
  "ACTIVITY_NOT_WITHDRAWABLE", // 409: withdraw on a SYSTEM task — it has no author and closes itself when its premise ends (§2.23, D-313 ruling 2). Only a literal admin meets it, because a system task's assignee meets 403 first. Raised by the withdraw door (E2-1)

  // deals — P3-E2 PR E2-1 (D-312; migration 0073), each raised by the
  // opportunities and pipeline-stages services
  // (apps/api/src/modules/sales/opportunities/, …/pipeline-stages/), by the
  // quote create and the link door for the deal side of a quote, and by the
  // task create for a deal subject:
  "OPPORTUNITY_NOT_FOUND", // 404: no such deal, another tenant's, or one outside the caller's deal door (§7 narrowing 15) — row-or-nothing, as every sales 404. Raised by every deal door, the quote create and link door, and the task create
  "OPPORTUNITY_NOT_OPEN", // 409: a move, a win or a loss of a won or lost deal, a PATCH of the value or the close month of one (the title and the owner stay editable), or a link of a quote to one. Checked under the deal's row lock
  "OPPORTUNITY_NOT_CLOSED", // 409: reopen on a deal that is open
  "OPPORTUNITY_STAGE_CHANGED", // 409: a move whose `from_stage_id` is not the deal's stage under the row lock — someone moved it first; `extensions.current_stage_id` names where it is (§3.11)
  "OPPORTUNITY_OWNER_NOT_ELIGIBLE", // 422: create or PATCH naming an owner who is not an active member holding `sales.opportunity.write` who can read the customer, or a non-admin naming anyone but themselves (§7 narrowing 16)
  "OPPORTUNITY_CUSTOMER_MISMATCH", // 409: a quote, an order or a source conversation of another customer than the deal's, or a conversation with no customer (D-312 ruling 5)
  "OPPORTUNITY_WON_BY_ORDER", // 409: reopen on a deal the customer's acceptance won while its order, or the order of any other linked quote the customer accepted, is not cancelled (D-312 ruling 9); checked under the deal's lock. The accepted residual of D-312 ruling 21
  "PIPELINE_NOT_CONFIGURED", // 409: create on a tenant with no stages — its installed pack predates the `pipeline_stage` kind (D-312 ruling 3); `extensions.remedy: "apply_pack_update"` names the previewed pack update that adds them (D-343)
  "PIPELINE_STAGE_NOT_FOUND", // 404: a stage id that is not one of the tenant's stages, on the create, the move and the stage PATCH
  "QUOTE_LINKED_ELSEWHERE", // 409: a link, a create or a win naming a quote already linked to another deal, or a win naming an order whose quote is — re-pointing needs an unlink first
  "ORDER_WON_ELSEWHERE", // 409: a win naming an order, or a converted quote whose order, another deal already won with — one order backs one deal, or the forecast would count its total twice. Backed by 0073's `opportunities_won_order_idx`, which answers the same code when two wins meet at once
  "QUOTE_NOT_CONVERTED", // 409: a manual win naming a quote that is not `converted` (only a converted quote records business)
  "CONVERSATION_NOT_FOUND", // 404: a create naming a `source_conversation_id` module 17 does not know in this tenant, another tenant's included (the read runs under the tenant's GUC, the only guard a no-FK column has)
  "SOURCE_CONVERSATION_UNVERIFIABLE", // 409: a create naming a `source_conversation_id` while module 17's port is unbound, so the conversation cannot be checked; logged as an error. A create without the field is unaffected

  // the customer record, P3-E2 PR E2-2a1 (D-323; migration 0077), each
  // raised by the notes, tags and assignments doors
  // (apps/api/src/modules/sales/notes/, …/tags/, …/assignments/). The other
  // ten E2-2 codes of spec 09 §5 join with the waves that raise them
  // (E2-2b's six and E2-2a2's four below), the D-198 pairing rule:
  "NOTE_NOT_FOUND", // 404: no such note, another tenant's, or a note on a customer the caller's customer door refuses (narrowing 22), row-or-nothing. Raised by the pin, unpin and redact doors
  "TAG_NOT_FOUND", // 404: no such tag or another tenant's. Raised by the rename, the archive and the tag attach
  "TAG_NAME_TAKEN", // 409: an ACTIVE tag of the tenant already has this name, compared on lower(btrim(name)) (`sales_tags_active_name_idx`, which both write doors map to this code). Raised by the create and the rename, judged under the tenant's tag lock
  "TAG_ARCHIVED", // 409: the attach names an archived tag. An archived tag stays on the customers that carry it and filters, and is never newly attached
  "TAG_LIMIT_REACHED", // 409: the tenant holds 200 active tags, counted under the tenant's tag lock (§2.27). Raised by the create
  "OWNER_CHANGED", // 409: `POST /v1/customers/{id}/assignments` with an `expected_owner_user_id` that differs from the owner read under the customer's row lock; `extensions.current_owner_user_id` names the owner (D-326 ruling 3). `ASSIGNMENT_EXISTS` answers first when the user already holds the role sent

  // the merge and the restore, P3-E2 PR E2-2b (D-324 rulings 4 to 16;
  // migration 0079), each raised by the merge and restore doors
  // (apps/api/src/modules/sales/customers/merge.service.ts and
  // customers.service.ts). The merge's 503 reuses the platform's
  // `RESOURCE_BUSY` with `Retry-After: 2`, and its two field checks reuse
  // `VALIDATION_FAILED` with the reasons `merge_credit_currency_split` and
  // `tax_id_format`:
  "MERGE_SAME_CUSTOMER", // 422: the preview or the merge names the path customer as its own loser. Checked after both customers' 404s and before any lock
  "MERGE_CUSTOMER_ARCHIVED", // 409: either customer is archived, read under both customers' FOR UPDATE locks, a merged loser included. Restore it first, or merge into the customer it was merged into
  "MERGE_STALE", // 409: under the locks, a customer's `xmin` differs from the version the body sent, or `gains_access` or the commercial attachment set the merge recomputes differs from what the body names (by ids for a holder of `sales.read_value`, by `commercial_attachment_count` for any other caller). Nothing is written, and the screen reads the preview again (§3.13)
  "MERGE_TAX_ID_CONFLICT", // 409: both customers carry a tax id and the two differ, because two tax ids are two legal entities (§9 item 35, D-334 ruling 1). The preview lists it in `blockers`
  "MERGE_CURRENCY_MISMATCH", // 409: the default currencies differ and the loser holds any quote, order or deal, open or finished, each of which carries a currency frozen from its customer (§9.13, §9 item 35). The preview lists it in `blockers`
  "MERGE_IMPORT_ORDER_REF_CONFLICT", // 409: both customers hold an imported order that is not cancelled with the same `import_order_ref`, which the merge's move would collide on (§9 item 44, D-353 ruling 5). The preview lists it in `blockers`
  "CUSTOMER_MERGED", // 409: `POST /v1/customers/{id}/restore` on a merged customer, because reviving it would give one company two live records again; `extensions.merged_into_customer_id` names the winner (§3.14)

  // sales attachments, P3-E2 PR E2-2a2 (D-323 rulings 8 to 12, D-335;
  // migration 0080), each raised by the attachment doors
  // (apps/api/src/modules/sales/attachments/). The attach reuses the
  // registry's `NOT_FOUND` for a missing file and its `FILE_NOT_STORED` for
  // an unverified one, and adds no sales twin of either, because each means
  // the same thing on both doors (`../files/schemas.ts`). A file whose bytes
  // are not its declared type is refused earlier, at `POST
  // /v1/files/{id}/store`, with the registry's `FILE_INTEGRITY_MISMATCH`:
  "ATTACHMENT_NOT_FOUND", // 404: no such attachment, another tenant's, one whose subject the caller's read door does not answer, or a `commercial` one read by a caller without `sales.read_value` (§7 narrowing 19), row-or-nothing. Raised by the download and the detach, the detach before its 403
  "ATTACHMENT_FILE_WRONG_PURPOSE", // 409: the attach names a stored file uploaded under another purpose than `sales_attachment`, such as a quote PDF or a thread's media, which this link would otherwise hand a second door (§2.29)
  "ATTACHMENT_FILE_NOT_YOURS", // 403: the attach names a stored `sales_attachment` file another member uploaded. Only the uploader links a file, so no member can link a file id it read elsewhere onto a subject it chose (§5's E2-2 row 10)
  "ATTACHMENT_ALREADY_LINKED", // 409: the file already has a link, active or detached, because one file has one link for good (`sales_attachments_file_idx`, §2.29). The loser of two concurrent attaches of one file meets it at the unique index

  // shared
  "PRODUCT_NOT_FOUND",
  "UOM_NOT_FOUND",
] as const;

export type SalesProblemCode = (typeof salesProblemCodes)[number];
